import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import XavvyFormRenderer, { validateCaseManagerRules } from './XavvyFormRenderer';

// ─── Styles ────────────────────────────────────────────────────────────────
const containerStyle = { display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' };
const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexWrap: 'wrap', gap: '8px' };
const bodyStyle = { flex: 1, overflow: 'auto', padding: '20px' };
const btnPrimary = { background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' };
const btnSecondary = { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' };
const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
const cardStyle = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', cursor: 'pointer', transition: 'border-color 0.2s' };
const badgeColors = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' };
const statusColors = { open: '#3b82f6', in_progress: '#f59e0b', under_review: '#8b5cf6', resolved: '#10b981', closed: '#6b7280', escalated: '#ef4444' };
const tabStyle = (active) => ({ padding: '8px 16px', cursor: 'pointer', borderBottom: active ? '2px solid var(--accent-color)' : '2px solid transparent', color: active ? 'var(--accent-color)' : 'var(--text-secondary)', fontWeight: active ? 600 : 400, fontSize: '13px', background: 'transparent', border: 'none', borderBottomWidth: '2px', borderBottomStyle: 'solid', borderBottomColor: active ? 'var(--accent-color)' : 'transparent' });
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContent = { background: 'var(--bg-secondary)', borderRadius: '12px', padding: '24px', width: '520px', maxWidth: '95vw', maxHeight: '80vh', overflow: 'auto', border: '1px solid var(--border-color)' };

function Badge({ children, color }) {
  return <span style={{ background: `${color}20`, color, padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' }}>{children}</span>;
}

function StatusBadge({ status }) {
  const color = statusColors[status] || '#6b7280';
  return <Badge color={color}>{(status || '').replace(/_/g, ' ')}</Badge>;
}

function PriorityBadge({ priority }) {
  const color = badgeColors[priority] || '#6b7280';
  return <Badge color={color}>{priority}</Badge>;
}

function CommentContent({ text }) {
  // Render @mentions with accent color
  if (!text) return null;
  const parts = text.split(/(@\w+)/g);
  return (
    <span>
      {parts.map((part, i) =>
        part.startsWith('@') ? (
          <span key={i} style={{ color: 'var(--accent-color)', fontWeight: 600 }}>{part}</span>
        ) : part
      )}
    </span>
  );
}

function SLAIndicator({ sla, label }) {
  if (!sla || sla.status === 'none') return null;
  const colors = { on_track: '#10b981', warning: '#f59e0b', breached: '#ef4444', met: '#6b7280' };
  const color = colors[sla.status] || '#6b7280';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color, display: 'inline-block' }} />
      <span style={{ color: 'var(--text-secondary)' }}>{label}:</span>
      <span style={{ color, fontWeight: 600 }}>{sla.remaining_human || sla.status}</span>
    </span>
  );
}

// ─── Kanban Board ──────────────────────────────────────────────────────────
function CaseKanbanBoard({ cases, onOpenCase, onStatusChange }) {
  const columns = [
    { key: 'open', label: 'Open', color: '#3b82f6' },
    { key: 'in_progress', label: 'In Progress', color: '#f59e0b' },
    { key: 'under_review', label: 'Under Review', color: '#8b5cf6' },
    { key: 'resolved', label: 'Resolved', color: '#10b981' },
    { key: 'closed', label: 'Closed', color: '#6b7280' },
  ];
  const [dragItem, setDragItem] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const handleDragStart = (e, c) => { setDragItem(c); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, colKey) => { e.preventDefault(); setDragOverCol(colKey); };
  const handleDragLeave = () => setDragOverCol(null);
  const handleDrop = (e, colKey) => {
    e.preventDefault();
    setDragOverCol(null);
    if (dragItem && dragItem.status !== colKey) {
      onStatusChange(dragItem.id, colKey);
    }
    setDragItem(null);
  };

  return (
    <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px', minHeight: '400px' }}>
      {columns.map(col => {
        const colCases = cases.filter(c => c.status === col.key);
        return (
          <div key={col.key}
            onDragOver={e => handleDragOver(e, col.key)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, col.key)}
            style={{
              flex: '1 0 220px', minWidth: '220px', maxWidth: '300px',
              background: dragOverCol === col.key ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
              borderRadius: '10px', padding: '10px', display: 'flex', flexDirection: 'column',
              border: dragOverCol === col.key ? `2px dashed ${col.color}` : '1px solid var(--border-color)',
              transition: 'background 0.15s, border 0.15s',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '0 4px' }}>
              <span style={{ fontWeight: 600, fontSize: '13px', color: col.color }}>{col.label}</span>
              <span style={{ fontSize: '11px', background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: '10px', color: 'var(--text-secondary)' }}>{colCases.length}</span>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
              {colCases.map(c => (
                <div key={c.id} draggable onDragStart={e => handleDragStart(e, c)}
                  onClick={() => onOpenCase(c.id)}
                  style={{
                    background: 'var(--bg-primary)', borderRadius: '8px', padding: '10px 12px',
                    border: '1px solid var(--border-color)', cursor: 'pointer',
                    opacity: dragItem?.id === c.id ? 0.4 : 1,
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = col.color}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                >
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>{c.case_number}</div>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '6px', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{c.title}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    <span>{c.case_type?.icon} {c.priority?.charAt(0).toUpperCase()}</span>
                    <span>{c.case_type?.name || ''}</span>
                  </div>
                </div>
              ))}
              {colCases.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>No cases</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Calendar View ─────────────────────────────────────────────────────────
function CaseCalendarView({ cases, onOpenCase }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const now = new Date();
  const viewDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthName = viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build case map by date (using SLA deadline or created_at)
  const casesByDate = useMemo(() => {
    const map = {};
    cases.forEach(c => {
      const dateStr = c.sla_resolution_deadline
        ? new Date(c.sla_resolution_deadline).toISOString().split('T')[0]
        : c.created_at
        ? new Date(c.created_at).toISOString().split('T')[0]
        : null;
      if (dateStr) {
        if (!map[dateStr]) map[dateStr] = [];
        map[dateStr].push(c);
      }
    });
    return map;
  }, [cases]);

  const priorityColor = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' };

  const cells = [];
  // Empty leading cells
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} style={{ minHeight: '90px', background: 'var(--bg-secondary)', borderRadius: '4px' }} />);
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayCases = casesByDate[dateStr] || [];
    const isToday = dateStr === new Date().toISOString().split('T')[0];
    cells.push(
      <div key={d} style={{
        minHeight: '90px', background: isToday ? 'var(--accent-color)08' : 'var(--bg-secondary)',
        borderRadius: '6px', padding: '4px 6px',
        border: isToday ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{ fontSize: '11px', fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--accent-color)' : 'var(--text-secondary)', marginBottom: '4px' }}>{d}</div>
        {dayCases.slice(0, 3).map(c => (
          <div key={c.id} onClick={() => onOpenCase(c.id)}
            style={{
              fontSize: '10px', padding: '2px 4px', marginBottom: '2px',
              borderRadius: '3px', cursor: 'pointer', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              background: `${priorityColor[c.priority] || '#3b82f6'}20`,
              color: priorityColor[c.priority] || '#3b82f6',
              borderLeft: `2px solid ${priorityColor[c.priority] || '#3b82f6'}`,
            }}
          >
            {c.case_number}: {c.title}
          </div>
        ))}
        {dayCases.length > 3 && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', padding: '0 4px' }}>+{dayCases.length - 3} more</div>}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <button onClick={() => setMonthOffset(m => m - 1)} style={btnSecondary}>← Prev</button>
        <span style={{ fontWeight: 600, fontSize: '15px' }}>{monthName}</span>
        <button onClick={() => setMonthOffset(m => m + 1)} style={btnSecondary}>Next →</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', padding: '4px' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {cells}
      </div>
    </div>
  );
}

// ─── Enhanced Dashboard ────────────────────────────────────────────────────
function CaseDashboard({ stats, workspaceId, getAuthHeaders }) {
  const [trends, setTrends] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded || !workspaceId) return;
    axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/analytics/trends?days=30`, getAuthHeaders())
      .then(r => setTrends(r.data))
      .catch(() => {});
  }, [expanded, workspaceId]);

  const slaRate = stats.sla_compliance?.rate_pct;
  const cards = [
    { label: 'Open', value: stats.open, color: '#3b82f6' },
    { label: 'In Progress', value: stats.in_progress, color: '#f59e0b' },
    { label: 'Resolved', value: stats.resolved, color: '#10b981' },
    { label: 'Overdue', value: stats.overdue, color: '#ef4444' },
    ...(slaRate != null ? [{ label: 'SLA Compliance', value: `${slaRate}%`, color: slaRate >= 90 ? '#10b981' : slaRate >= 70 ? '#f59e0b' : '#ef4444' }] : []),
  ];
  // Trend chart: mini bar chart for last 14 days
  const trendBars = trends?.series?.slice(-14) || [];
  const maxCreated = Math.max(1, ...trendBars.map(d => d.created));

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '12px' }}>
        {cards.map(c => (
          <div key={c.label} style={{ ...cardStyle, cursor: 'default', borderLeft: `3px solid ${c.color}`, padding: '12px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: c.color }}>{c.value ?? 0}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Expand for analytics */}
      <button onClick={() => setExpanded(e => !e)}
        style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '12px', padding: '4px 0', marginBottom: '8px' }}>
        {expanded ? '▾ Hide Analytics' : '▸ Show Analytics'}
      </button>

      {expanded && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          {/* Trend chart */}
          <div style={{ ...cardStyle, cursor: 'default', padding: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Cases — Last 14 Days</div>
            {trendBars.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '80px' }}>
                {trendBars.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }} title={`${d.date}\nCreated: ${d.created}\nResolved: ${d.resolved}`}>
                    <div style={{ width: '100%', maxWidth: '20px', background: '#3b82f6', borderRadius: '2px 2px 0 0', height: `${Math.max(2, (d.created / maxCreated) * 60)}px`, transition: 'height 0.3s' }} />
                    <div style={{ width: '100%', maxWidth: '20px', background: '#10b981', borderRadius: '0 0 2px 2px', height: `${Math.max(1, (d.resolved / maxCreated) * 60)}px`, transition: 'height 0.3s', marginTop: '-1px' }} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '12px' }}>Loading...</div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '10px', color: 'var(--text-tertiary)' }}>
              <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#3b82f6', borderRadius: '2px', marginRight: '4px' }} />Created</span>
              <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#10b981', borderRadius: '2px', marginRight: '4px' }} />Resolved</span>
            </div>
          </div>

          {/* By Priority breakdown */}
          <div style={{ ...cardStyle, cursor: 'default', padding: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Open by Priority</div>
            {Object.entries(stats.by_priority || {}).map(([p, count]) => {
              const pColor = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' }[p] || '#6b7280';
              const maxP = Math.max(1, ...Object.values(stats.by_priority || {}));
              return (
                <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', width: '60px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{p}</span>
                  <div style={{ flex: 1, height: '14px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(count / maxP) * 100}%`, background: pColor, borderRadius: '4px', transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: pColor, minWidth: '24px', textAlign: 'right' }}>{count}</span>
                </div>
              );
            })}
            {Object.keys(stats.by_priority || {}).length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>No open cases</div>
            )}
          </div>

          {/* By Type breakdown */}
          <div style={{ ...cardStyle, cursor: 'default', padding: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Open by Type</div>
            {(stats.by_type || []).map((t, i) => {
              const maxT = Math.max(1, ...(stats.by_type || []).map(x => x.count));
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', flex: '0 0 100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{t.name}</span>
                  <div style={{ flex: 1, height: '14px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(t.count / maxT) * 100}%`, background: 'var(--accent-color)', borderRadius: '4px' }} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--accent-color)', minWidth: '24px', textAlign: 'right' }}>{t.count}</span>
                </div>
              );
            })}
            {(stats.by_type || []).length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>No open cases</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dynamic Field Renderer ───────────────────────────────────────────────
function DynamicField({ field, value, onChange }) {
  const lbl = { fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' };
  switch (field.type) {
    case 'textarea':
      return (
        <div>
          <label style={lbl}>{field.label}{field.required ? ' *' : ''}</label>
          <textarea value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} placeholder={field.label} />
        </div>
      );
    case 'number':
      return (
        <div>
          <label style={lbl}>{field.label}{field.required ? ' *' : ''}</label>
          <input type="number" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} style={inputStyle} placeholder={field.label} />
        </div>
      );
    case 'date':
      return (
        <div>
          <label style={lbl}>{field.label}{field.required ? ' *' : ''}</label>
          <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle} />
        </div>
      );
    case 'dropdown':
      return (
        <div>
          <label style={lbl}>{field.label}{field.required ? ' *' : ''}</label>
          <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">— Select —</option>
            {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    case 'multi_select':
      return (
        <div>
          <label style={lbl}>{field.label}{field.required ? ' *' : ''}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {(field.options || []).map(o => {
              const selected = (value || []).includes(o);
              return (
                <button key={o} type="button" onClick={() => {
                  const arr = value || [];
                  onChange(selected ? arr.filter(v => v !== o) : [...arr, o]);
                }} style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', cursor: 'pointer', border: '1px solid var(--border-color)', background: selected ? 'var(--accent-color)' : 'var(--bg-tertiary)', color: selected ? '#fff' : 'var(--text-primary)' }}>
                  {o}
                </button>
              );
            })}
          </div>
        </div>
      );
    case 'checkbox':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} style={{ width: '16px', height: '16px' }} />
          {field.label}{field.required ? ' *' : ''}
        </label>
      );
    default: // text
      return (
        <div>
          <label style={lbl}>{field.label}{field.required ? ' *' : ''}</label>
          <input value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle} placeholder={field.label} />
        </div>
      );
  }
}

// ─── Create Case Modal ─────────────────────────────────────────────────────
function CreateCaseModal({ caseTypes, onClose, onCreate, authHeaders }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [caseTypeId, setCaseTypeId] = useState(caseTypes[0]?.id || '');
  const [priority, setPriority] = useState('medium');
  const [customFields, setCustomFields] = useState({});
  const [intakeFormDef, setIntakeFormDef] = useState(null);
  const [intakeFormValues, setIntakeFormValues] = useState({});

  const selectedType = caseTypes.find(ct => ct.id === caseTypeId);
  const schemaFields = selectedType?.schema_json?.fields || [];
  const intakeFormId = selectedType?.form_config_json?.intake_form_id;

  // Load the configured intake form whenever the case type changes.
  useEffect(() => {
    setIntakeFormDef(null);
    setIntakeFormValues({});
    if (!intakeFormId || !authHeaders) return;
    let cancelled = false;
    axios.get(`${API_BASE_URL}/xavvy-forms/${intakeFormId}`, authHeaders())
      .then(res => {
        if (cancelled) return;
        const defn = res.data?.definition;
        setIntakeFormDef(defn ? (typeof defn === 'string' ? JSON.parse(defn) : defn) : null);
      })
      .catch(() => { if (!cancelled) setIntakeFormDef(null); });
    return () => { cancelled = true; };
  }, [intakeFormId, authHeaders]);

  const handleTypeChange = (id) => {
    setCaseTypeId(id);
    setCustomFields({});
    setIntakeFormValues({});
  };

  const setFieldValue = (key, val) => {
    setCustomFields(prev => ({ ...prev, [key]: val }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !caseTypeId) return;
    // Source of truth for custom_fields: the intake form if configured, else
    // the per-field DynamicField inputs. The form renderer enforces its own
    // required-field validation, so only the fallback path needs it here.
    let finalCustomFields;
    if (intakeFormDef) {
      finalCustomFields = mapFormValuesToCaseKeys(intakeFormValues, selectedType?.schema_json, intakeFormDef);
    } else {
      for (const f of schemaFields) {
        if (f.required) {
          const v = customFields[f.key];
          if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
            alert(`"${f.label}" is required.`);
            return;
          }
        }
      }
      finalCustomFields = customFields;
    }
    const data = { case_type_id: caseTypeId, title: title.trim(), description, priority };
    if (finalCustomFields && Object.keys(finalCustomFields).length > 0) data.custom_fields_json = finalCustomFields;
    onCreate(data);
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Create New Case</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Case Type *</label>
            <select value={caseTypeId} onChange={e => handleTypeChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              {caseTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.icon} {ct.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} placeholder="Brief case title" autoFocus />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} placeholder="Describe the case..." />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          {/* Intake form takes priority when the case type configures one;
              otherwise fall back to per-field inputs from the schema. */}
          {intakeFormDef ? (
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', marginTop: '4px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                {selectedType?.icon} {selectedType?.name} Intake
              </div>
              <XavvyFormRenderer
                definition={intakeFormDef}
                initialValues={intakeFormValues}
                onValuesChange={(vals) => setIntakeFormValues(vals)}
              />
            </div>
          ) : schemaFields.length > 0 && (
            <>
              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', marginTop: '4px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {selectedType?.icon} {selectedType?.name} Fields
                </div>
              </div>
              {schemaFields.map(f => (
                <DynamicField key={f.key} field={f} value={customFields[f.key]} onChange={v => setFieldValue(f.key, v)} />
              ))}
            </>
          )}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
            <button type="submit" style={{ ...btnPrimary, opacity: !title.trim() ? 0.5 : 1 }} disabled={!title.trim()}>Create Case</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Case Detail View ──────────────────────────────────────────────────────
// Walk a xavvy form definition and collect leaf field defs (anything with a `name`).
// Mirrors the backend's CaseTaskStepNode._collect_field_defs so both sides agree
// on what constitutes a "field" inside nested containers.
function collectFormFields(formDef) {
  const out = [];
  const walk = (node) => {
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (!node || typeof node !== 'object') return;
    if (typeof node.name === 'string' && node.name) out.push(node);
    ['elements', 'fields', 'columns', 'tabs', 'children', 'items'].forEach(k => {
      if (k in node) walk(node[k]);
    });
  };
  walk(formDef?.elements || []);
  return out;
}

// Prefill: map case custom_fields values → form field names, honoring per-field
// case_field_key / task_field_key / no_writeback mappings set by the picker.
// On the detail form there is no task in scope, so task-mapped fields are
// left untouched (they shouldn't be on a detail form in the first place).
function mapCaseValuesToFormNames(caseFields, formDef) {
  if (!caseFields || !formDef) return caseFields || {};
  const fields = collectFormFields(formDef);
  const cfLower = {};
  Object.entries(caseFields).forEach(([k, v]) => { cfLower[k.toLowerCase()] = v; });
  const result = {};
  fields.forEach(f => {
    if (f.no_writeback) return;
    if ((f.task_field_key || '').trim()) return;
    const caseKey = (f.case_field_key || '').trim() || f.name;
    if (caseFields[caseKey] !== undefined) result[f.name] = caseFields[caseKey];
    else if (cfLower[caseKey.toLowerCase()] !== undefined) result[f.name] = cfLower[caseKey.toLowerCase()];
  });
  // Carry through any case values not covered by a form field — lets the
  // case keep custom_fields set outside the form (e.g. from a workflow step).
  Object.entries(caseFields).forEach(([k, v]) => {
    if (result[k] === undefined && !fields.some(f => f.name === k)) result[k] = v;
  });
  return result;
}

// Writeback: map form values → case custom_fields keys. Uses the picker's
// case_field_key when set; task-mapped and no_writeback fields are ignored.
// Legacy fallback: field name → case schema key by case-insensitive match.
function mapFormValuesToCaseKeys(formValues, caseSchema, formDef) {
  if (!formValues) return {};
  const fields = formDef ? collectFormFields(formDef) : [];
  const fieldByName = {};
  fields.forEach(f => { fieldByName[f.name] = f; });
  const schemaLower = {};
  (caseSchema?.fields || []).forEach(f => { schemaLower[f.key.toLowerCase()] = f.key; });
  const result = {};
  Object.entries(formValues).forEach(([formName, v]) => {
    const fdef = fieldByName[formName];
    if (fdef?.no_writeback) return;
    if ((fdef?.task_field_key || '').trim()) return;
    const explicitKey = (fdef?.case_field_key || '').trim();
    if (explicitKey) { result[explicitKey] = v; return; }
    result[schemaLower[formName.toLowerCase()] || formName] = v;
  });
  return result;
}

function CaseDetail({ caseId, workspaceId, authHeaders, onBack, onRefresh }) {
  const [caseData, setCaseData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [comments, setComments] = useState([]);
  const [relations, setRelations] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  // Document management state
  const [documents, setDocuments] = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [docFolder, setDocFolder] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [docVersions, setDocVersions] = useState({});  // { docId: [versions] }
  const [expandedDocId, setExpandedDocId] = useState(null);
  // Phase 5: form integration
  const [detailFormDef, setDetailFormDef] = useState(null);  // form definition when detail_form_id is configured
  // Task management state
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', assignee_id: '', due_date: '', priority: 'medium' });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({});
  // Comment edit state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  // Relation add state
  const [showAddRelation, setShowAddRelation] = useState(false);
  const [relationSearch, setRelationSearch] = useState('');
  const [relationSearchResults, setRelationSearchResults] = useState([]);
  const [relationType, setRelationType] = useState('related');
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Generic item delete confirmation: { type: 'task'|'document', id, label }
  const [pendingItemDelete, setPendingItemDelete] = useState(null);
  // Escalation state
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateForm, setEscalateForm] = useState({ reason: '', new_priority: '' });
  // Phase 8: Stage tracking
  const [stages, setStages] = useState([]);
  // Timeline filter
  const [timelineFilter, setTimelineFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const [cRes, aRes, cmRes, rRes, dRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}`, authHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/activity${timelineFilter ? `?action_type=${encodeURIComponent(timelineFilter)}` : ''}`, authHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/comments`, authHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/relations`, authHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents`, authHeaders()),
      ]);
      setCaseData(cRes.data);
      setActivity(aRes.data.items || []);
      setComments(cmRes.data || []);
      setRelations(rRes.data || []);
      setDocuments(dRes.data || []);

      // Phase 5: Load form definition if detail_form_id is configured
      const formCfg = cRes.data.case_type?.form_config_json;
      if (formCfg?.detail_form_id) {
        try {
          const formRes = await axios.get(`${API_BASE_URL}/xavvy-forms/${formCfg.detail_form_id}`, authHeaders());
          setDetailFormDef(formRes.data?.definition ? (typeof formRes.data.definition === 'string' ? JSON.parse(formRes.data.definition) : formRes.data.definition) : null);
        } catch { setDetailFormDef(null); }
      } else {
        setDetailFormDef(null);
      }
      // Phase 8: Load stage progress
      try {
        const stgRes = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/stages`, authHeaders());
        setStages(Array.isArray(stgRes.data) ? stgRes.data : []);
      } catch { setStages([]); }
    } catch (err) { console.error('Failed to load case', err); }
    setLoading(false);
  }, [caseId, workspaceId, timelineFilter]);

  useEffect(() => { load(); }, [load]);

  // Fetch workspace members for assignee picker
  useEffect(() => {
    if (!workspaceId) return;
    axios.get(`${API_BASE_URL}/workspaces/${workspaceId}`, authHeaders())
      .then(res => setMembers(res.data.members || []))
      .catch(() => setMembers([]));
  }, [workspaceId]);

  const handleAdvanceStage = async (force = false) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/advance-stage`, { force }, authHeaders());
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to advance stage';
      if (!force && detail.includes('not completed') && confirm(detail + '\n\nAdvance anyway?')) {
        handleAdvanceStage(true);
      } else {
        alert(detail);
      }
    }
  };

  const handleStatusTransition = async (newStatus) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/status`, { status: newStatus }, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Status transition failed'); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/comments`, { content: newComment, is_internal: isInternal }, authHeaders());
      setNewComment('');
      load();
    } catch (err) { console.error('Failed to add comment', err); }
  };

  const handleToggleWatch = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/watch`, {}, authHeaders());
      load();
    } catch (err) { console.error(err); }
  };

  const handleTaskComplete = async (taskId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/tasks/${taskId}/complete`, {}, authHeaders());
      load();
    } catch (err) { console.error(err); }
  };

  const startEditing = () => {
    const c = caseData;
    setEditForm({
      title: c.title || '',
      description: c.description || '',
      priority: c.priority || 'medium',
      custom_fields: { ...(c.custom_fields_json || {}) },
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    // Check for unsaved changes before discarding
    if (caseData && editing) {
      const c = caseData;
      const orig = {
        title: c.title || '',
        description: c.description || '',
        priority: c.priority || 'medium',
        custom_fields: c.custom_fields_json || {},
      };
      const current = {
        title: editForm.title || '',
        description: editForm.description || '',
        priority: editForm.priority || 'medium',
        custom_fields: editForm.custom_fields || {},
      };
      if (JSON.stringify(current) !== JSON.stringify(orig)) {
        if (!window.confirm('You have unsaved changes. Are you sure you want to discard them?')) return;
      }
    }
    setEditing(false);
    setEditForm({});
  };

  const saveEdits = async () => {
    const c = caseData;
    const schemaFields = c.case_type?.schema_json?.fields || [];
    // Validate required custom fields
    for (const f of schemaFields) {
      if (f.required) {
        const v = editForm.custom_fields?.[f.key];
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          alert(`"${f.label}" is required.`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const patch = {};
      if (editForm.title !== c.title) patch.title = editForm.title;
      if (editForm.description !== c.description) patch.description = editForm.description;
      if (editForm.priority !== c.priority) patch.priority = editForm.priority;
      if (Object.keys(editForm.custom_fields || {}).length > 0 ||
          Object.keys(c.custom_fields_json || {}).length > 0) {
        patch.custom_fields_json = editForm.custom_fields;
      }
      if (Object.keys(patch).length > 0) {
        await axios.patch(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}`,
          patch,
          authHeaders()
        );
        await load();
      }
      setEditing(false);
      setEditForm({});
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save changes');
    }
    setSaving(false);
  };

  // Document handlers
  const handleDocUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setDocUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        if (docFolder) form.append('folder', docFolder);
        await axios.post(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents`,
          form,
          { ...authHeaders(), headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } }
        );
      }
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    setDocUploading(false);
    e.target.value = '';
  };

  const handleDocDownload = async (doc, version) => {
    try {
      const url = `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${doc.id}/download${version ? `?version=${version}` : ''}`;
      const res = await axios.get(url, { ...authHeaders(), responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) { alert('Download failed'); }
  };

  const handleDocCheckout = async (docId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${docId}/checkout`, {}, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Checkout failed'); }
  };

  const handleDocCheckin = async (docId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const form = new FormData();
      form.append('file', file);
      try {
        await axios.post(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${docId}/checkin`,
          form,
          { ...authHeaders(), headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } }
        );
        load();
      } catch (err) { alert(err.response?.data?.detail || 'Check-in failed'); }
    };
    input.click();
  };

  const handleDocCancelCheckout = async (docId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${docId}/cancel-checkout`, {}, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Cancel checkout failed'); }
  };

  const handleDocDelete = (docId, docName) => {
    setPendingItemDelete({ type: 'document', id: docId, label: docName || 'this document' });
  };

  const loadVersions = async (docId) => {
    if (expandedDocId === docId) { setExpandedDocId(null); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${docId}/versions`, authHeaders());
      setDocVersions(prev => ({ ...prev, [docId]: res.data || [] }));
      setExpandedDocId(docId);
    } catch { setExpandedDocId(null); }
  };

  // ── Task CRUD handlers ──────────────────────────────────────────────────
  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      const body = { title: newTask.title.trim(), description: newTask.description, priority: newTask.priority };
      if (newTask.assignee_id) body.assignee_id = Number(newTask.assignee_id);
      if (newTask.due_date) body.due_date = newTask.due_date;
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/tasks`, body, authHeaders());
      setNewTask({ title: '', description: '', assignee_id: '', due_date: '', priority: 'medium' });
      setShowAddTask(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to add task'); }
  };

  const handleUpdateTask = async (taskId) => {
    try {
      const body = {};
      if (editTaskForm.title !== undefined) body.title = editTaskForm.title;
      if (editTaskForm.description !== undefined) body.description = editTaskForm.description;
      if (editTaskForm.assignee_id !== undefined) body.assignee_id = editTaskForm.assignee_id === '' ? null : Number(editTaskForm.assignee_id);
      if (editTaskForm.due_date !== undefined) body.due_date = editTaskForm.due_date || null;
      if (editTaskForm.priority !== undefined) body.priority = editTaskForm.priority;
      if (editTaskForm.status !== undefined) body.status = editTaskForm.status;
      if (editTaskForm.depends_on !== undefined) body.depends_on = editTaskForm.depends_on;
      await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/tasks/${taskId}`, body, authHeaders());
      setEditingTaskId(null);
      setEditTaskForm({});
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update task'); }
  };

  const handleDeleteTask = (taskId, taskTitle) => {
    setPendingItemDelete({ type: 'task', id: taskId, label: taskTitle || 'this task' });
  };
  const confirmItemDelete = async () => {
    if (!pendingItemDelete) return;
    const { type, id } = pendingItemDelete;
    setPendingItemDelete(null);
    try {
      if (type === 'task') {
        await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/tasks/${id}`, authHeaders());
      } else if (type === 'document') {
        await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${id}`, authHeaders());
      }
      load();
    } catch (err) { alert(err.response?.data?.detail || `Failed to delete ${type}`); }
  };

  // ── Comment edit/delete handlers ───────────────────────────────────────
  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/comments/${commentId}`, { content: editCommentText }, authHeaders());
      setEditingCommentId(null);
      setEditCommentText('');
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to edit comment'); }
  };

  const handleDeleteComment = async (commentId) => {
    if (!confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/comments/${commentId}`, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete comment'); }
  };

  // ── Relation create/delete handlers ────────────────────────────────────
  const handleSearchRelation = async (q) => {
    setRelationSearch(q);
    if (!q || q.length < 2) { setRelationSearchResults([]); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases?search=${encodeURIComponent(q)}&page_size=10`, authHeaders());
      setRelationSearchResults((res.data.items || []).filter(c => c.id !== caseId));
    } catch { setRelationSearchResults([]); }
  };

  const handleAddRelation = async (relatedCaseId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/relations`, { related_case_id: relatedCaseId, relation_type: relationType }, authHeaders());
      setShowAddRelation(false);
      setRelationSearch('');
      setRelationSearchResults([]);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to link case'); }
  };

  const handleDeleteRelation = async (relationId) => {
    if (!confirm('Remove this case relation?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/relations/${relationId}`, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to remove relation'); }
  };

  // ── Escalate handler ───────────────────────────────────────────────────
  const handleEscalate = async () => {
    try {
      const params = new URLSearchParams();
      if (escalateForm.reason) params.set('reason', escalateForm.reason);
      if (escalateForm.new_priority) params.set('new_priority', escalateForm.new_priority);
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/escalate?${params}`, {}, authHeaders());
      setShowEscalate(false);
      setEscalateForm({ reason: '', new_priority: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Escalation failed'); }
  };

  // ── Delete case handler ────────────────────────────────────────────────
  const handleDeleteCase = () => setShowDeleteConfirm(true);
  const confirmDeleteCase = async () => {
    setShowDeleteConfirm(false);
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}`, authHeaders());
      setEditing(false);
      onBack();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete case'); }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const fileIcon = (contentType) => {
    if (!contentType) return '📄';
    if (contentType.startsWith('image/')) return '🖼';
    if (contentType.includes('pdf')) return '📕';
    if (contentType.includes('word') || contentType.includes('document')) return '📝';
    if (contentType.includes('sheet') || contentType.includes('excel')) return '📊';
    if (contentType.includes('zip') || contentType.includes('archive')) return '📦';
    if (contentType.includes('text')) return '📃';
    return '📄';
  };

  const canPreview = (contentType) => {
    if (!contentType) return false;
    return contentType.startsWith('image/') || contentType === 'application/pdf' || contentType.startsWith('text/');
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading case...</div>;
  if (!caseData) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Case not found</div>;

  const c = caseData;

  // Parse allowed transitions from case type's status workflow
  const workflow = c.case_type?.status_workflow_json;
  const allowedTransitions = workflow?.transitions?.[c.status] || [];
  const statusLabelMap = {};
  const statusColorMap = {};
  if (workflow?.statuses) {
    workflow.statuses.forEach(s => { statusLabelMap[s.key] = s.label; statusColorMap[s.key] = s.color; });
  }

  // Schema fields for custom field display
  const schemaFields = c.case_type?.schema_json?.fields || [];
  const fieldLabelMap = {};
  schemaFields.forEach(f => { fieldLabelMap[f.key] = f; });

  const tabs = ['summary', 'timeline', 'tasks', 'documents', 'comments', 'related'];

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={onBack} style={{ ...btnSecondary, padding: '6px 12px' }}>← Back</button>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{c.case_number}</span>
          <StatusBadge status={c.status} />
          <PriorityBadge priority={c.priority} />
          {c.case_type && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{c.case_type.icon} {c.case_type.name}</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={handleToggleWatch} style={btnSecondary} title={c.is_watching ? 'Unwatch' : 'Watch'}>
            {c.is_watching ? '👁 Watching' : '👁 Watch'}
          </button>
          <button onClick={() => setShowEscalate(true)} style={{ ...btnSecondary, color: '#f59e0b', borderColor: '#f59e0b' }} title="Escalate case">
            Escalate
          </button>
          <button onClick={handleDeleteCase} style={{ ...btnSecondary, color: '#ef4444', borderColor: '#ef4444' }} title="Delete case">
            Delete
          </button>
        </div>
      </div>

      {/* Title & Meta */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '18px' }}>{c.title}</h2>
        {c.description && <p style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{c.description}</p>}
        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-secondary)', flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Created: {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</span>
          <SLAIndicator sla={c.sla_response} label="Response" />
          <SLAIndicator sla={c.sla_resolution} label="Resolution" />
          <span>Source: {c.source}</span>
          <span>Watchers: {c.watcher_count}</span>
        </div>
        {/* SLA countdown bar */}
        {c.sla_resolution?.status && c.sla_resolution.status !== 'none' && c.sla_resolution.status !== 'met' && (
          <div style={{ marginTop: '8px' }}>
            {(() => {
              const s = c.sla_resolution;
              const colors = { on_track: '#10b981', warning: '#f59e0b', breached: '#ef4444' };
              const bgColor = colors[s.status] || '#6b7280';
              const total = c.sla_resolution_deadline && c.created_at
                ? (new Date(c.sla_resolution_deadline) - new Date(c.created_at)) / 1000
                : 0;
              const elapsed = total > 0 ? Math.max(0, Math.min(1, (total - (s.remaining_seconds || 0)) / total)) : 0;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, height: '6px', background: 'var(--bg-tertiary)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, elapsed * 100)}%`, height: '100%', background: bgColor, borderRadius: '3px', transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: bgColor, whiteSpace: 'nowrap' }}>
                    {s.remaining_human}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Quick status buttons — driven by case type's allowed transitions */}
      <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginRight: '4px' }}>Move to:</span>
        {allowedTransitions.length > 0 ? (
          allowedTransitions.map(s => (
            <button key={s} onClick={() => handleStatusTransition(s)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px', borderColor: statusColorMap[s] || 'var(--border-color)', color: statusColorMap[s] || 'var(--text-primary)' }}>
              {statusLabelMap[s] || s.replace(/_/g, ' ')}
            </button>
          ))
        ) : (
          // Fallback if no workflow defined — show all statuses except current
          ['open', 'in_progress', 'under_review', 'resolved', 'closed'].filter(s => s !== c.status).map(s => (
            <button key={s} onClick={() => handleStatusTransition(s)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px', textTransform: 'capitalize' }}>
              {s.replace(/_/g, ' ')}
            </button>
          ))
        )}
      </div>

      {/* Phase 8: Stage Stepper */}
      {stages.length > 0 && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflowX: 'auto' }}>
            {stages.map((stg, idx) => {
              const isActive = stg.is_current;
              const isCompleted = stg.status === 'completed';
              const isPending = stg.status === 'pending';
              const isSkipped = stg.status === 'skipped';
              const stgColor = stg.stage_color || (isCompleted ? '#10b981' : isActive ? 'var(--accent-color)' : '#6b7280');
              const bgColor = isCompleted ? '#10b98120' : isActive ? 'var(--accent-color)15' : 'transparent';
              const borderColor = isCompleted ? '#10b981' : isActive ? 'var(--accent-color)' : 'var(--border-color)';
              return (
                <React.Fragment key={stg.stage_id}>
                  {idx > 0 && (
                    <div style={{ width: '24px', height: '2px', background: isCompleted || isActive ? stgColor : 'var(--border-color)', flexShrink: 0 }} />
                  )}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                    borderRadius: '20px', border: `1.5px solid ${borderColor}`,
                    background: bgColor, flexShrink: 0, position: 'relative',
                  }} title={stg.stage_name + (stg.sla_deadline ? `\nSLA: ${new Date(stg.sla_deadline).toLocaleString()}` : '')}>
                    <span style={{ fontSize: '13px' }}>
                      {isCompleted ? '\u2713' : isSkipped ? '\u2013' : isActive ? '\u25CF' : '\u25CB'}
                    </span>
                    <span style={{
                      fontSize: '12px', fontWeight: isActive ? 600 : 400,
                      color: isCompleted ? '#10b981' : isActive ? 'var(--accent-color)' : isPending ? 'var(--text-tertiary)' : '#6b7280',
                      whiteSpace: 'nowrap',
                    }}>
                      {stg.stage_name}
                    </span>
                    {isActive && stg.total_tasks > 0 && (
                      <span style={{ fontSize: '10px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {stg.completed_tasks}/{stg.total_tasks}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
            {/* Advance button — shown for current stage with manual entry_mode on next, or when all tasks done */}
            {stages.some(s => s.is_current) && !stages[stages.length - 1]?.is_current && (
              <button
                onClick={() => handleAdvanceStage(false)}
                style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px', marginLeft: '8px', flexShrink: 0 }}
                title="Advance to next stage"
              >
                Advance &rarr;
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', padding: '0 20px' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={tabStyle(activeTab === t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'tasks' && c.tasks ? ` (${c.tasks.length})` : ''}
            {t === 'comments' ? ` (${comments.length})` : ''}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={bodyStyle}>
        {activeTab === 'summary' && (
          <div>
            {/* ── Edit Mode ─────────────────────────────────────────── */}
            {editing ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px' }}>Edit Case</h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={cancelEditing} style={btnSecondary} disabled={saving}>Cancel</button>
                    <button onClick={saveEdits} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* Built-in fields */}
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Title *</label>
                    <input value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Description</label>
                    <textarea value={editForm.description || ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Priority</label>
                    <select value={editForm.priority || 'medium'} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                      {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  {/* Custom fields: use form renderer if detail form configured, else DynamicField */}
                  {detailFormDef ? (
                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '14px', marginTop: '2px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                        {c.case_type?.icon} {c.case_type?.name} Fields
                      </div>
                      <XavvyFormRenderer
                        definition={detailFormDef}
                        initialValues={mapCaseValuesToFormNames(editForm.custom_fields || {}, detailFormDef)}
                        onValuesChange={(vals) => {
                          const caseSchema = c.case_type?.schema_json;
                          setEditForm(prev => ({ ...prev, custom_fields: mapFormValuesToCaseKeys(vals, caseSchema, detailFormDef) }));
                        }}
                      />
                    </div>
                  ) : schemaFields.length > 0 && (
                    <>
                      <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '14px', marginTop: '2px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          {c.case_type?.icon} {c.case_type?.name} Fields
                        </div>
                      </div>
                      {schemaFields.map(f => (
                        <DynamicField
                          key={f.key}
                          field={f}
                          value={editForm.custom_fields?.[f.key]}
                          onChange={v => setEditForm(prev => ({ ...prev, custom_fields: { ...prev.custom_fields, [f.key]: v } }))}
                        />
                      ))}
                    </>
                  )}
                </div>
              </div>
            ) : (
              /* ── Read-Only Mode ──────────────────────────────────── */
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px' }}>Details</h4>
                  <button onClick={startEditing} style={{ ...btnSecondary, padding: '5px 12px', fontSize: '12px' }}>✎ Edit</button>
                </div>
                {/* Built-in fields as read-only cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ ...cardStyle, cursor: 'default' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Title</div>
                    <div style={{ fontSize: '13px' }}>{c.title || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</div>
                  </div>
                  <div style={{ ...cardStyle, cursor: 'default' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Priority</div>
                    <div style={{ fontSize: '13px' }}><PriorityBadge priority={c.priority} /></div>
                  </div>
                  {c.description && (
                    <div style={{ ...cardStyle, cursor: 'default', gridColumn: 'span 2' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Description</div>
                      <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{c.description}</div>
                    </div>
                  )}
                </div>
                {/* Phase 5: Render form layout OR default custom fields */}
                {detailFormDef ? (
                  <div style={{ marginBottom: '16px' }}>
                    <XavvyFormRenderer
                      definition={detailFormDef}
                      initialValues={mapCaseValuesToFormNames(c.custom_fields_json || {}, detailFormDef)}
                      readOnly={true}
                    />
                  </div>
                ) : (
                  /* Default custom fields layout */
                  (schemaFields.length > 0 || c.custom_fields_json) && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                      {schemaFields.length > 0 ? (
                        schemaFields.map(f => {
                          const v = c.custom_fields_json?.[f.key];
                          let display;
                          if (v === undefined || v === null || v === '') display = <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                          else if (f.type === 'checkbox') display = v ? 'Yes' : 'No';
                          else if (f.type === 'multi_select' && Array.isArray(v)) display = v.join(', ') || <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                          else display = String(v);
                          return (
                            <div key={f.key} style={{ ...cardStyle, cursor: 'default' }}>
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{f.label}</div>
                              <div style={{ fontSize: '13px' }}>{display}</div>
                            </div>
                          );
                        })
                      ) : c.custom_fields_json ? (
                        Object.entries(c.custom_fields_json).map(([k, v]) => (
                          <div key={k} style={{ ...cardStyle, cursor: 'default' }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{k}</div>
                            <div style={{ fontSize: '13px' }}>{Array.isArray(v) ? v.join(', ') : String(v)}</div>
                          </div>
                        ))
                      ) : null}
                    </div>
                  )
                )}
              </div>
            )}
            {c.resolution_notes && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>Resolution</h4>
                <div style={cardStyle}>
                  {c.resolution_category && <Badge color="#10b981">{c.resolution_category}</Badge>}
                  <p style={{ margin: '8px 0 0', fontSize: '13px' }}>{c.resolution_notes}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (() => {
          const activityIcons = {
            case_created: { icon: '+', color: '#10b981' }, status_changed: { icon: '~', color: '#3b82f6' },
            priority_changed: { icon: '!', color: '#f59e0b' }, assigned: { icon: '>', color: '#8b5cf6' },
            escalated: { icon: '^', color: '#ef4444' }, comment_added: { icon: '#', color: '#6366f1' },
            task_created: { icon: 'T', color: '#14b8a6' }, task_completed: { icon: 'V', color: '#10b981' },
            task_status_changed: { icon: 'T', color: '#f59e0b' }, task_deleted: { icon: 'X', color: '#ef4444' },
            document_uploaded: { icon: 'D', color: '#06b6d4' }, document_deleted: { icon: 'D', color: '#ef4444' },
            field_changed: { icon: 'F', color: '#8b5cf6' }, watcher_added: { icon: 'W', color: '#6b7280' },
            stage_completed: { icon: 'S', color: '#10b981' }, stage_entered: { icon: 'S', color: '#3b82f6' },
            relation_added: { icon: 'L', color: '#8b5cf6' }, relation_removed: { icon: 'L', color: '#ef4444' },
          };
          const filterOptions = [
            { value: '', label: 'All Activity' },
            { value: 'status_changed,priority_changed,assigned,escalated', label: 'Status & Assignment' },
            { value: 'comment_added', label: 'Comments' },
            { value: 'task_created,task_completed,task_status_changed,task_deleted', label: 'Tasks' },
            { value: 'document_uploaded,document_deleted', label: 'Documents' },
            { value: 'field_changed', label: 'Field Changes' },
            { value: 'stage_completed,stage_entered', label: 'Stages' },
          ];
          // Group by date
          const grouped = {};
          activity.forEach(a => {
            const d = a.created_at ? new Date(a.created_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown';
            (grouped[d] = grouped[d] || []).push(a);
          });
          const formatValue = (v) => {
            if (v === null || v === undefined) return '';
            if (typeof v === 'object') return JSON.stringify(v);
            return String(v).replace(/_/g, ' ');
          };
          return (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <select value={timelineFilter || ''} onChange={e => { setTimelineFilter(e.target.value); }} style={{ ...inputStyle, maxWidth: '220px', cursor: 'pointer' }}>
                  {filterOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {activity.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No activity yet.</p>}
              {Object.entries(grouped).map(([date, items]) => (
                <div key={date} style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>{date}</div>
                  <div style={{ borderLeft: '2px solid var(--border-color)', marginLeft: '10px', paddingLeft: '0' }}>
                    {items.map(a => {
                      const ai = activityIcons[a.action_type] || { icon: '*', color: 'var(--accent-color)' };
                      return (
                        <div key={a.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '6px 0', marginLeft: '-11px' }}>
                          <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: `${ai.color}20`, color: ai.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0, border: `2px solid ${ai.color}40` }}>{ai.icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', lineHeight: '20px' }}>
                              <strong>{a.user_name || 'System'}</strong>{' '}
                              <span style={{ color: 'var(--text-secondary)' }}>{a.action_type.replace(/_/g, ' ')}</span>
                              {a.old_value != null && a.new_value != null && (
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  {' '}<span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{formatValue(a.old_value)}</span>
                                  {' → '}<strong style={{ color: 'var(--text-primary)' }}>{formatValue(a.new_value)}</strong>
                                </span>
                              )}
                              {a.new_value != null && a.old_value == null && (
                                <span style={{ color: 'var(--text-secondary)' }}>{' '}<strong style={{ color: 'var(--text-primary)' }}>{formatValue(a.new_value)}</strong></span>
                              )}
                              {a.details?.task_title && <span style={{ color: 'var(--text-secondary)' }}>{' — '}{a.details.task_title}</span>}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '1px' }}>{a.created_at ? new Date(a.created_at).toLocaleTimeString() : ''}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {activeTab === 'tasks' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Discretionary task palette (Phase 7) */}
            <DiscretionaryTaskPalette
              workspaceId={workspaceId}
              caseId={c.id}
              authHeaders={authHeaders}
              onRefresh={load}
            />
            {/* Add task button / form */}
            {showAddTask ? (
              <div style={{ ...cardStyle, cursor: 'default', display: 'flex', flexDirection: 'column', gap: '8px', borderColor: 'var(--accent-color)' }}>
                <input value={newTask.title} onChange={e => setNewTask(f => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="Task title *" autoFocus />
                <textarea value={newTask.description} onChange={e => setNewTask(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }} placeholder="Description (optional)" />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <select value={newTask.assignee_id} onChange={e => setNewTask(f => ({ ...f, assignee_id: e.target.value }))} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                    <option value="">Unassigned</option>
                    {members.map(m => <option key={m.user_id} value={m.user_id}>{m.username}</option>)}
                  </select>
                  <select value={newTask.priority} onChange={e => setNewTask(f => ({ ...f, priority: e.target.value }))} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                    {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                  <input type="date" value={newTask.due_date} onChange={e => setNewTask(f => ({ ...f, due_date: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setShowAddTask(false); setNewTask({ title: '', description: '', assignee_id: '', due_date: '', priority: 'medium' }); }} style={btnSecondary}>Cancel</button>
                  <button onClick={handleAddTask} style={btnPrimary} disabled={!newTask.title.trim()}>Add Task</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddTask(true)} style={{ ...btnSecondary, alignSelf: 'flex-start', fontSize: '12px' }}>+ Quick Task</button>
            )}
            {(!c.tasks || c.tasks.length === 0) && !showAddTask && <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No tasks.</p>}
            {(c.tasks || []).map(t => (
              <div key={t.id} style={{ ...cardStyle, cursor: 'default' }}>
                {editingTaskId === t.id ? (
                  /* Edit task inline */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input value={editTaskForm.title ?? ''} onChange={e => setEditTaskForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="Task title" />
                    <textarea value={editTaskForm.description ?? ''} onChange={e => setEditTaskForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: '40px', resize: 'vertical' }} placeholder="Description" />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <select value={editTaskForm.status ?? ''} onChange={e => setEditTaskForm(f => ({ ...f, status: e.target.value }))} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                        {['pending', 'in_progress', 'completed', 'skipped'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                      </select>
                      <select value={editTaskForm.assignee_id ?? ''} onChange={e => setEditTaskForm(f => ({ ...f, assignee_id: e.target.value }))} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                        <option value="">Unassigned</option>
                        {members.map(m => <option key={m.user_id} value={m.user_id}>{m.username}</option>)}
                      </select>
                      <select value={editTaskForm.priority ?? 'medium'} onChange={e => setEditTaskForm(f => ({ ...f, priority: e.target.value }))} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                        {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                      </select>
                      <input type="date" value={editTaskForm.due_date ?? ''} onChange={e => setEditTaskForm(f => ({ ...f, due_date: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                    </div>
                    {/* Dependency picker */}
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Depends on (blocked until these complete):</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                        {(editTaskForm.depends_on || []).map(depId => {
                          const dep = (c.tasks || []).find(x => x.id === depId);
                          return dep ? (
                            <span key={depId} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', background: dep.status === 'completed' ? '#10b98120' : '#f59e0b20', color: dep.status === 'completed' ? '#10b981' : '#f59e0b' }}>
                              {dep.status === 'completed' ? 'V ' : '~ '}{dep.title}
                              <span onClick={() => setEditTaskForm(f => ({ ...f, depends_on: (f.depends_on || []).filter(d => d !== depId) }))} style={{ cursor: 'pointer', marginLeft: '2px', opacity: 0.7 }}>x</span>
                            </span>
                          ) : null;
                        })}
                      </div>
                      <select
                        value=""
                        onChange={e => {
                          if (e.target.value && !(editTaskForm.depends_on || []).includes(e.target.value)) {
                            setEditTaskForm(f => ({ ...f, depends_on: [...(f.depends_on || []), e.target.value] }));
                          }
                        }}
                        style={{ ...inputStyle, cursor: 'pointer', fontSize: '12px' }}
                      >
                        <option value="">+ Add dependency...</option>
                        {(c.tasks || []).filter(x => x.id !== t.id && !(editTaskForm.depends_on || []).includes(x.id)).map(x => (
                          <option key={x.id} value={x.id}>{x.title} ({x.status})</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button onClick={() => { setEditingTaskId(null); setEditTaskForm({}); }} style={btnSecondary}>Cancel</button>
                      <button onClick={() => handleUpdateTask(t.id)} style={btnPrimary}>Save</button>
                    </div>
                  </div>
                ) : (
                  /* Read-only task */
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {(() => {
                      const isBlocked = t.depends_on?.length > 0 && t.depends_on.some(depId => {
                        const dep = (c.tasks || []).find(x => x.id === depId);
                        return dep && dep.status !== 'completed';
                      });
                      const isWorkflowDriven = !!t.task_type_id;
                      const disabled = isBlocked || isWorkflowDriven;
                      const tipText = isWorkflowDriven
                        ? 'Workflow task — auto-completes when the workflow finishes'
                        : isBlocked ? 'Blocked by dependencies' : '';
                      return (
                        <input
                          type="checkbox"
                          checked={t.status === 'completed'}
                          disabled={disabled && t.status !== 'completed'}
                          onChange={() => {
                            if (t.status === 'completed') return;
                            if (isWorkflowDriven) return;
                            if (isBlocked) { alert('Cannot complete — this task has unfinished dependencies.'); return; }
                            handleTaskComplete(t.id);
                          }}
                          style={{ width: '18px', height: '18px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled && t.status !== 'completed' ? 0.4 : 1 }}
                          title={tipText}
                        />
                      );
                    })()}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', textDecoration: t.status === 'completed' ? 'line-through' : 'none', opacity: t.status === 'completed' ? 0.6 : 1 }}>{t.title}</span>
                        {t.depends_on?.length > 0 && t.depends_on.some(depId => { const dep = (c.tasks || []).find(x => x.id === depId); return dep && dep.status !== 'completed'; }) && (
                          <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px', background: '#ef444420', color: '#ef4444', fontWeight: 600 }}>BLOCKED</span>
                        )}
                      </div>
                      {t.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{t.description}</div>}
                      {/* Dependency chain */}
                      {t.depends_on?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {t.depends_on.map(depId => {
                            const dep = (c.tasks || []).find(x => x.id === depId);
                            if (!dep) return null;
                            const done = dep.status === 'completed';
                            return (
                              <span key={depId} style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px', background: done ? '#10b98115' : '#f59e0b15', color: done ? '#10b981' : '#f59e0b' }}>
                                {done ? 'V' : '~'} {dep.title}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                        <StatusBadge status={t.status} />
                        <PriorityBadge priority={t.priority || 'medium'} />
                        {t.assignee_name && <span>{'-> '}{t.assignee_name}</span>}
                        {t.due_date && <span>Due: {new Date(t.due_date).toLocaleDateString()}</span>}
                      </div>
                      {/* Phase 7: Task workflow progress tracker */}
                      {(t.task_type_id || t.workflow_run_id) && (
                        <div style={{ marginTop: '6px' }}>
                          <TaskProgressTracker task={t} />
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      {/* Hide edit/delete for workflow-driven tasks */}
                      {!t.task_type_id && (
                        <>
                          <button onClick={() => { setEditingTaskId(t.id); setEditTaskForm({ title: t.title, description: t.description || '', status: t.status, assignee_id: t.assignee_id ?? '', due_date: t.due_date ? t.due_date.slice(0, 10) : '', priority: t.priority || 'medium', depends_on: t.depends_on || [] }); }} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} title="Edit">✎</button>
                          <button onClick={() => handleDeleteTask(t.id, t.title)} style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }} title="Delete">✕</button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            {/* Upload area */}
            <div style={{ marginBottom: '16px', padding: '16px', border: '2px dashed var(--border-color)', borderRadius: '8px', textAlign: 'center', background: 'var(--bg-tertiary)' }}>
              <div style={{ fontSize: '28px', marginBottom: '6px' }}>📎</div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Drag & drop files or click to upload
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  value={docFolder}
                  onChange={e => setDocFolder(e.target.value)}
                  style={{ ...inputStyle, width: '160px', padding: '5px 8px', fontSize: '12px' }}
                  placeholder="Folder (optional)"
                />
                <label style={{ ...btnPrimary, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', opacity: docUploading ? 0.5 : 1 }}>
                  {docUploading ? 'Uploading...' : '+ Upload Files'}
                  <input
                    type="file"
                    multiple
                    onChange={handleDocUpload}
                    style={{ display: 'none' }}
                    disabled={docUploading}
                  />
                </label>
              </div>
            </div>

            {/* Required documents checklist */}
            {c.case_type?.required_documents_json && c.case_type.required_documents_json.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Required Documents</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {c.case_type.required_documents_json.map((rd, idx) => {
                    const label = typeof rd === 'string' ? rd : rd.label || rd.key;
                    const key = (typeof rd === 'string' ? rd : rd.key || rd.label).toLowerCase().trim();
                    const found = documents.some(d =>
                      (d.folder && d.folder.toLowerCase().trim() === key) ||
                      d.filename.toLowerCase().includes(key)
                    );
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                        <span style={{ color: found ? '#10b981' : '#ef4444', fontSize: '14px' }}>{found ? '✓' : '✗'}</span>
                        <span style={{ color: found ? 'var(--text-primary)' : '#ef4444' }}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Document list */}
            {documents.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px', padding: '20px' }}>No documents attached yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {documents.map(doc => (
                  <div key={doc.id} style={{ ...cardStyle, cursor: 'default', padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '22px' }}>{fileIcon(doc.content_type)}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600 }}>{doc.filename}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>v{doc.version}</span>
                          {doc.folder && <Badge color="#8b5cf6">{doc.folder}</Badge>}
                          {doc.locked_by && (
                            <Badge color="#f59e0b">Locked by {doc.locked_by_name || 'user'}</Badge>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                          <span>{formatBytes(doc.size_bytes)}</span>
                          <span>{doc.content_type || 'unknown'}</span>
                          <span>Uploaded by {doc.uploaded_by_name || '—'}</span>
                          <span>{doc.created_at ? new Date(doc.created_at).toLocaleString() : ''}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        {canPreview(doc.content_type) && (
                          <button onClick={() => setPreviewDoc(doc)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} title="Preview">👁</button>
                        )}
                        <button onClick={() => handleDocDownload(doc)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} title="Download">⬇</button>
                        {!doc.locked_by && (
                          <button onClick={() => handleDocCheckout(doc.id)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} title="Check out">🔒</button>
                        )}
                        {doc.locked_by && (
                          <>
                            <button onClick={() => handleDocCheckin(doc.id)} style={{ ...btnPrimary, padding: '4px 8px', fontSize: '11px' }} title="Check in new version">📤</button>
                            <button onClick={() => handleDocCancelCheckout(doc.id)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} title="Cancel checkout">🔓</button>
                          </>
                        )}
                        <button onClick={() => loadVersions(doc.id)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} title="Version history">📋</button>
                        <button onClick={() => handleDocDelete(doc.id, doc.filename)} style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }} title="Delete">✕</button>
                      </div>
                    </div>
                    {/* Version history expansion */}
                    {expandedDocId === doc.id && docVersions[doc.id] && (
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)' }}>Version History</div>
                        {docVersions[doc.id].map(v => (
                          <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0', fontSize: '12px' }}>
                            <span style={{ fontWeight: 600, minWidth: '30px' }}>v{v.version_number}</span>
                            <span style={{ color: 'var(--text-secondary)' }}>{formatBytes(v.size_bytes)}</span>
                            <span style={{ color: 'var(--text-secondary)' }}>{v.uploaded_by_name || '—'}</span>
                            <span style={{ color: 'var(--text-tertiary)', flex: 1 }}>{v.change_notes || ''}</span>
                            <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>{v.created_at ? new Date(v.created_at).toLocaleString() : ''}</span>
                            <button onClick={() => handleDocDownload(doc, v.version_number)} style={{ ...btnSecondary, padding: '2px 6px', fontSize: '10px' }}>⬇</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Preview modal */}
            {previewDoc && (
              <div style={modalOverlay} onClick={() => setPreviewDoc(null)}>
                <div style={{ ...modalContent, width: '80vw', maxWidth: '900px', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px' }}>{previewDoc.filename}</h3>
                    <button onClick={() => setPreviewDoc(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
                  </div>
                  <div style={{ textAlign: 'center', overflow: 'auto', maxHeight: '75vh' }}>
                    {previewDoc.content_type?.startsWith('image/') && (
                      <img
                        src={`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${previewDoc.id}/download`}
                        style={{ maxWidth: '100%', borderRadius: '6px' }}
                        alt={previewDoc.filename}
                      />
                    )}
                    {previewDoc.content_type === 'application/pdf' && (
                      <iframe
                        src={`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/documents/${previewDoc.id}/download`}
                        style={{ width: '100%', height: '70vh', border: 'none', borderRadius: '6px' }}
                        title={previewDoc.filename}
                      />
                    )}
                    {previewDoc.content_type?.startsWith('text/') && (
                      <div style={{ textAlign: 'left', padding: '12px', background: 'var(--bg-primary)', borderRadius: '6px', fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'pre-wrap', maxHeight: '65vh', overflow: 'auto' }}>
                        Loading preview...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div>
            {/* Comment input */}
            <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                placeholder="Add a comment..."
              />
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} /> Internal note
                </label>
                <div style={{ flex: 1 }} />
                <button onClick={handleAddComment} style={{ ...btnPrimary, padding: '6px 14px' }} disabled={!newComment.trim()}>Post Comment</button>
              </div>
            </div>
            {/* Comment list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {comments.map(cm => (
                <div key={cm.id} style={{ ...cardStyle, cursor: 'default', borderLeft: `3px solid ${cm.is_internal ? '#f59e0b' : '#3b82f6'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>{cm.user_name}</span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{cm.created_at ? new Date(cm.created_at).toLocaleString() : ''}</span>
                      {editingCommentId !== cm.id && (
                        <>
                          <button onClick={() => { setEditingCommentId(cm.id); setEditCommentText(cm.content); }} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '11px', padding: '2px' }} title="Edit">✎</button>
                          <button onClick={() => handleDeleteComment(cm.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '11px', padding: '2px', opacity: 0.6 }} title="Delete">✕</button>
                        </>
                      )}
                    </div>
                  </div>
                  {cm.is_internal && <Badge color="#f59e0b">Internal</Badge>}
                  {editingCommentId === cm.id ? (
                    <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <textarea value={editCommentText} onChange={e => setEditCommentText(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setEditingCommentId(null); setEditCommentText(''); }} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>Cancel</button>
                        <button onClick={() => handleEditComment(cm.id)} style={{ ...btnPrimary, padding: '4px 10px', fontSize: '11px' }} disabled={!editCommentText.trim()}>Save</button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ margin: '6px 0 0', fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}><CommentContent text={cm.content} /></p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'related' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Add relation */}
            {showAddRelation ? (
              <div style={{ ...cardStyle, cursor: 'default', display: 'flex', flexDirection: 'column', gap: '8px', borderColor: 'var(--accent-color)' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select value={relationType} onChange={e => setRelationType(e.target.value)} style={{ ...inputStyle, width: '140px', cursor: 'pointer' }}>
                    <option value="related">Related</option>
                    <option value="parent">Parent</option>
                    <option value="child">Child</option>
                    <option value="duplicate">Duplicate</option>
                  </select>
                  <input value={relationSearch} onChange={e => handleSearchRelation(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Search cases by title or case number..." autoFocus />
                  <button onClick={() => { setShowAddRelation(false); setRelationSearch(''); setRelationSearchResults([]); }} style={btnSecondary}>Cancel</button>
                </div>
                {relationSearchResults.length > 0 && (
                  <div style={{ maxHeight: '200px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {relationSearchResults.map(rc => (
                      <div key={rc.id} onClick={() => handleAddRelation(rc.id)} style={{ ...cardStyle, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-color)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                      >
                        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)' }}>{rc.case_number}</span>
                        <span style={{ flex: 1, fontSize: '12px' }}>{rc.title}</span>
                        <StatusBadge status={rc.status} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => setShowAddRelation(true)} style={{ ...btnSecondary, alignSelf: 'flex-start', fontSize: '12px' }}>+ Link Case</button>
            )}
            {relations.length === 0 && !showAddRelation && <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No related cases.</p>}
            {relations.map(r => (
              <div key={r.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px', cursor: 'default' }}>
                <Badge color="#8b5cf6">{r.relation_type}</Badge>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r.related_case_number}</span>
                <span style={{ flex: 1, fontSize: '13px' }}>{r.related_case_title}</span>
                <StatusBadge status={r.related_case_status} />
                <button onClick={() => handleDeleteRelation(r.id)} style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }} title="Remove">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div style={modalOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <div style={{ ...modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Confirm</h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Permanently delete this case and all its data? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={btnSecondary}>Cancel</button>
              <button onClick={confirmDeleteCase} style={{ ...btnPrimary, background: '#ef4444' }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Item delete confirmation modal (task / document) */}
      {pendingItemDelete && (
        <div style={modalOverlay} onClick={() => setPendingItemDelete(null)}>
          <div style={{ ...modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Delete {pendingItemDelete.type} "{pendingItemDelete.label}"? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setPendingItemDelete(null)} style={btnSecondary}>Cancel</button>
              <button onClick={confirmItemDelete} style={{ ...btnPrimary, background: '#ef4444' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Escalation modal */}
      {showEscalate && (
        <div style={modalOverlay} onClick={() => setShowEscalate(false)}>
          <div style={modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Escalate Case</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Reason *</label>
                <textarea value={escalateForm.reason} onChange={e => setEscalateForm(f => ({ ...f, reason: e.target.value }))} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} placeholder="Why is this case being escalated?" autoFocus />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>New Priority (optional)</label>
                <select value={escalateForm.new_priority} onChange={e => setEscalateForm(f => ({ ...f, new_priority: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Keep current ({c.priority})</option>
                  {['critical', 'high', 'medium', 'low'].filter(p => p !== c.priority).map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowEscalate(false)} style={btnSecondary}>Cancel</button>
                <button onClick={handleEscalate} style={{ ...btnPrimary, background: '#f59e0b' }} disabled={!escalateForm.reason.trim()}>Escalate</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── My Tasks Inbox ───────────────────────────────────────────────────────
function MyTasksInbox({ workspaceId, authHeaders, onOpenCase }) {
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const params = statusFilter ? `?status_filter=${statusFilter}` : '';
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/my-tasks${params}`, authHeaders());
      setTasks(res.data || []);
    } catch { setTasks([]); }
    setLoading(false);
  }, [workspaceId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (taskId, caseId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/tasks/${taskId}/complete`, {}, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to complete task'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '15px' }}>My Tasks</h3>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, maxWidth: '140px', cursor: 'pointer' }}>
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading...</div>
      ) : tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>No tasks assigned to you.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {tasks.map(t => (
            <div key={t.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="checkbox"
                checked={t.status === 'completed'}
                onChange={() => t.status !== 'completed' && handleComplete(t.id, t.case_id)}
                style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 500, textDecoration: t.status === 'completed' ? 'line-through' : 'none', opacity: t.status === 'completed' ? 0.6 : 1 }}>{t.title}</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                  <StatusBadge status={t.status} />
                  <PriorityBadge priority={t.priority} />
                  {t.case_number && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenCase(t.case_id); }}
                      style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '11px', padding: 0 }}
                    >
                      {t.case_number}: {t.case_title}
                    </button>
                  )}
                  {t.due_date && <span>Due: {new Date(t.due_date).toLocaleDateString()}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── My Work (Phase 7 — unified inbox: work items + flat tasks) ────────────
function MyWorkInbox({ workspaceId, authHeaders, onOpenCase, initialWorkItemId }) {
  const [data, setData] = useState({ work_items: [], flat_tasks: [] });
  const [baskets, setBaskets] = useState([]);
  const [activeBasket, setActiveBasket] = useState('my_work'); // 'my_work' or basket id
  const [basketItems, setBasketItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWorkItem, setSelectedWorkItem] = useState(initialWorkItemId || null);
  // Inline filters
  const [inboxFilters, setInboxFilters] = useState({ status: '', priority: '', search: '' });
  const [showFilters, setShowFilters] = useState(false);
  // Worker UI filter values for baskets (field → value)
  const [uiFilterValues, setUiFilterValues] = useState({});
  // Column sort state for basket table
  const [colSort, setColSort] = useState({ key: '', dir: 'asc' });

  // Auto-open work item from notification deep-link
  useEffect(() => {
    if (initialWorkItemId) setSelectedWorkItem(initialWorkItemId);
  }, [initialWorkItemId]);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [myWork, basketList] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/my-work`, authHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets`, authHeaders()).catch(() => ({ data: [] })),
      ]);
      setData(myWork.data || { work_items: [], flat_tasks: [] });
      setBaskets(basketList.data || []);
    } catch { setData({ work_items: [], flat_tasks: [] }); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // Load basket items when a specific basket is selected or UI filter values change
  const loadBasketItems = useCallback(async () => {
    if (activeBasket === 'my_work' || !activeBasket) { setBasketItems(null); return; }
    try {
      const params = new URLSearchParams();
      // Build runtime UI filter selections
      const activeUiFilters = Object.entries(uiFilterValues).filter(([, v]) => v).map(([field, value]) => ({ field, value }));
      if (activeUiFilters.length > 0) params.set('ui_field_filters', JSON.stringify(activeUiFilters));
      const url = `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets/${activeBasket}/items${params.toString() ? '?' + params.toString() : ''}`;
      const res = await axios.get(url, authHeaders());
      setBasketItems(res.data);
      // Set initial sort indicator from basket's default_sort (server already sorted)
      if (res.data.default_sort) {
        setColSort({ key: res.data.default_sort.field || '', dir: res.data.default_sort.direction || 'asc' });
      }
    } catch { setBasketItems(null); }
  }, [activeBasket, workspaceId, uiFilterValues]);

  useEffect(() => { loadBasketItems(); }, [loadBasketItems]);

  // Reset all state when switching baskets
  useEffect(() => {
    setUiFilterValues({});
    setColSort({ key: '', dir: 'asc' });
    setInboxFilters({ status: '', priority: '', search: '' });
    if (activeBasket !== 'my_work') setBasketItems(null);
  }, [activeBasket]);

  const handleClaim = async (wiId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${wiId}/claim`, {}, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to claim'); }
  };

  const handleComplete = async (wiId, formData, decision) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${wiId}/complete`, { fields: formData || {}, decision: decision || 'submit' }, authHeaders());
      setSelectedWorkItem(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to complete'); }
  };

  const handleRelease = async (wiId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${wiId}/release`, {}, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to release'); }
  };

  const handleFlatComplete = async (taskId, caseId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/tasks/${taskId}/complete`, {}, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to complete task'); }
  };

  // If a work item is selected for full detail
  if (selectedWorkItem) {
    return (
      <WorkItemDetail
        workspaceId={workspaceId}
        workItemId={selectedWorkItem}
        authHeaders={authHeaders}
        onBack={() => { setSelectedWorkItem(null); load(); }}
        onComplete={handleComplete}
        onOpenCase={onOpenCase}
      />
    );
  }

  const rawItems = activeBasket === 'my_work'
    ? data.work_items
    : (basketItems?.items || []);
  const rawFlatTasks = activeBasket === 'my_work' ? data.flat_tasks : [];
  const basketColumns = (activeBasket !== 'my_work' && basketItems?.columns) ? basketItems.columns : [];

  // Apply inline filters
  const uiFilterActive = Object.values(uiFilterValues).some(v => v);
  const filterActive = inboxFilters.status || inboxFilters.priority || inboxFilters.search || uiFilterActive;
  const searchLower = (inboxFilters.search || '').toLowerCase();
  const items = rawItems.filter(wi => {
    if (inboxFilters.status && wi.status !== inboxFilters.status) return false;
    if (inboxFilters.priority && wi.priority !== inboxFilters.priority) return false;
    if (searchLower) {
      const hay = `${wi.step_name || ''} ${wi.case?.case_number || ''} ${wi.case?.title || ''} ${wi.claimed_by_name || ''}`.toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    return true;
  });
  const flatTasks = rawFlatTasks.filter(t => {
    if (inboxFilters.status && t.status !== inboxFilters.status) return false;
    if (inboxFilters.priority && t.priority !== inboxFilters.priority) return false;
    if (searchLower) {
      const hay = `${t.title || ''} ${t.case_number || ''}`.toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    return true;
  });
  // Apply column sort (client-side for basket table view)
  if (colSort.key && basketColumns.length > 0) {
    items.sort((a, b) => {
      const av = a._columns?.[colSort.key] ?? '';
      const bv = b._columns?.[colSort.key] ?? '';
      let cmp = 0;
      if (av == null && bv == null) cmp = 0;
      else if (av == null) cmp = 1;
      else if (bv == null) cmp = -1;
      else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return colSort.dir === 'desc' ? -cmp : cmp;
    });
  }
  const totalItems = items.length + flatTasks.length;
  const totalUnfiltered = rawItems.length + rawFlatTasks.length;

  const slaColor = (deadline) => {
    if (!deadline) return null;
    const remaining = new Date(deadline) - new Date();
    if (remaining < 0) return '#ef4444';
    if (remaining < 2 * 3600 * 1000) return '#f59e0b';
    return '#22c55e';
  };

  const slaText = (deadline) => {
    if (!deadline) return null;
    const ms = new Date(deadline) - new Date();
    if (ms < 0) return 'BREACHED';
    const hours = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div style={{ display: 'flex', gap: '16px' }}>
      {/* Left sidebar — in-basket list */}
      <div style={{ width: '200px', flexShrink: 0, borderRight: '1px solid var(--border-color)', paddingRight: '12px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '8px' }}>In-Baskets</div>
        <button
          onClick={() => setActiveBasket('my_work')}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
            background: activeBasket === 'my_work' ? 'var(--accent-color)' : 'transparent',
            color: activeBasket === 'my_work' ? '#fff' : 'var(--text-primary)',
            border: 'none', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', fontSize: '13px', textAlign: 'left', marginBottom: '4px',
          }}
        >
          <span>My Work</span>
          <span style={{ fontSize: '11px', opacity: 0.8 }}>{data.work_items.length + data.flat_tasks.length}</span>
        </button>
        {baskets.map(b => (
          <button
            key={b.id}
            onClick={() => setActiveBasket(b.id)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
              background: activeBasket === b.id ? 'var(--accent-color)' : 'transparent',
              color: activeBasket === b.id ? '#fff' : 'var(--text-primary)',
              border: 'none', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', fontSize: '13px', textAlign: 'left', marginBottom: '4px',
            }}
          >
            <span>{b.icon && b.icon !== 'inbox' ? b.icon + ' ' : ''}{b.name}</span>
            <span style={{ fontSize: '11px', opacity: 0.8 }}>{b.item_count ?? ''}</span>
          </button>
        ))}
      </div>

      {/* Main area — work item table */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>
            {activeBasket === 'my_work' ? 'My Work' : baskets.find(b => b.id === activeBasket)?.name || 'In-Basket'}
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {filterActive ? `${totalItems} of ${totalUnfiltered}` : totalItems} item{totalItems !== 1 ? 's' : ''}
            </span>
            <button onClick={() => setShowFilters(f => !f)} style={{ ...btnSecondary, padding: '3px 10px', fontSize: '11px', background: showFilters || filterActive ? 'var(--accent-color)' : undefined, color: showFilters || filterActive ? '#fff' : undefined }}>
              {filterActive ? 'Filtered' : 'Filter'}
            </button>
          </div>
        </div>

        {/* Unified filter bar */}
        {showFilters && (() => {
          const uiFilters = (activeBasket !== 'my_work' && basketItems?.ui_filters) || [];
          const filterInputStyle = { padding: '5px 8px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'inherit' };
          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', padding: '8px 10px', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
              <input
                value={inboxFilters.search}
                onChange={e => setInboxFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Search..."
                style={{ ...filterInputStyle, width: '150px' }}
              />
              <select value={inboxFilters.status} onChange={e => setInboxFilters(f => ({ ...f, status: e.target.value }))} style={filterInputStyle}>
                <option value="">All Statuses</option>
                <option value="available">Available</option>
                <option value="claimed">Claimed</option>
                <option value="completed">Completed</option>
                <option value="skipped">Skipped</option>
              </select>
              <select value={inboxFilters.priority} onChange={e => setInboxFilters(f => ({ ...f, priority: e.target.value }))} style={filterInputStyle}>
                <option value="">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              {/* Admin-configured case field filters */}
              {uiFilters.map(uf => (
                <div key={uf.field} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{uf.label || uf.field}:</label>
                  {uf.options?.length > 0 ? (
                    <select value={uiFilterValues[uf.field] || ''} onChange={e => setUiFilterValues(prev => ({ ...prev, [uf.field]: e.target.value }))} style={filterInputStyle}>
                      <option value="">All</option>
                      {uf.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      value={uiFilterValues[uf.field] || ''}
                      onChange={e => setUiFilterValues(prev => ({ ...prev, [uf.field]: e.target.value }))}
                      placeholder={uf.label || uf.field}
                      style={{ ...filterInputStyle, width: '120px' }}
                    />
                  )}
                </div>
              ))}
              {filterActive && (
                <button onClick={() => { setInboxFilters({ status: '', priority: '', search: '' }); setUiFilterValues({}); }} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '11px', padding: '4px', whiteSpace: 'nowrap' }}>
                  Clear all
                </button>
              )}
            </div>
          );
        })()}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading...</div>
        ) : totalItems === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>No work items.</div>
        ) : basketColumns.length > 0 ? (
          /* ── Dynamic column table for in-baskets ── */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  {basketColumns.map(col => (
                    <th key={col.key}
                      onClick={col.sortable ? () => setColSort(s => s.key === col.key ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col.key, dir: 'asc' }) : undefined}
                      style={{
                        textAlign: 'left', padding: '8px 10px', fontSize: '11px', fontWeight: 600,
                        textTransform: 'uppercase', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                        width: col.width ? `${col.width}px` : undefined,
                        cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none',
                      }}>
                      {col.label || col.key}
                      {col.sortable && colSort.key === col.key && (
                        <span style={{ marginLeft: '4px', fontSize: '10px' }}>{colSort.dir === 'asc' ? '▲' : '▼'}</span>
                      )}
                      {col.sortable && colSort.key !== col.key && (
                        <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.3 }}>⇅</span>
                      )}
                    </th>
                  ))}
                  <th style={{ width: '90px', padding: '8px 10px' }} />
                </tr>
              </thead>
              <tbody>
                {items.map(wi => (
                  <tr key={wi.id}
                      onClick={() => setSelectedWorkItem(wi.id)}
                      style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    {basketColumns.map(col => {
                      const raw = wi._columns?.[col.key] ?? '';
                      let display = raw;
                      if (col.format === 'countdown' && raw) {
                        display = (
                          <span style={{ fontWeight: 600, color: slaColor(raw) }}>
                            {slaText(raw)}
                          </span>
                        );
                      } else if (col.format === 'relative_time' && raw) {
                        const ms = Date.now() - new Date(raw).getTime();
                        const mins = Math.floor(ms / 60000);
                        display = mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
                      } else if (col.format === 'currency' && raw !== '' && raw != null) {
                        display = typeof raw === 'number' ? `$${raw.toLocaleString()}` : String(raw);
                      } else if (col.key === 'status') {
                        display = <StatusBadge status={String(raw)} />;
                      } else if (col.key === 'priority') {
                        display = <PriorityBadge priority={String(raw)} />;
                      } else if (raw != null && typeof raw === 'object') {
                        display = JSON.stringify(raw);
                      } else {
                        display = raw != null ? String(raw) : '—';
                      }
                      return (
                        <td key={col.key} style={{
                          padding: '8px 10px', whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis', maxWidth: col.width ? `${col.width}px` : '250px',
                        }}>
                          {display}
                        </td>
                      );
                    })}
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      {wi.status === 'available' && (
                        <button onClick={(e) => { e.stopPropagation(); handleClaim(wi.id); }}
                                style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>Claim</button>
                      )}
                      {wi.status === 'claimed' && (
                        <button onClick={(e) => { e.stopPropagation(); handleRelease(wi.id); }}
                                style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px', background: 'var(--bg-secondary)' }}>Release</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* ── Card layout for My Work and baskets without columns ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {/* Work Items */}
            {items.map(wi => (
              <div key={wi.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                   onClick={() => setSelectedWorkItem(wi.id)}>
                {/* Priority color bar */}
                <div style={{ width: '4px', height: '40px', borderRadius: '2px', background: wi.priority === 'critical' ? '#ef4444' : wi.priority === 'high' ? '#f59e0b' : wi.priority === 'medium' ? '#3b82f6' : '#6b7280', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>{wi.step_name}</span>
                    <StatusBadge status={wi.status} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '3px', fontSize: '11px', color: 'var(--text-secondary)', flexWrap: 'wrap', alignItems: 'center' }}>
                    {wi.case?.case_type_name && (
                      <span style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: 500 }}>
                        {wi.case.case_type_icon || '📋'} {wi.case.case_type_name}
                      </span>
                    )}
                    {wi.task_type_name && (
                      <span style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: 500 }}>
                        {wi.task_type_name}
                      </span>
                    )}
                    {wi.case?.case_number && (
                      <button onClick={(e) => { e.stopPropagation(); onOpenCase(wi.case_id); }}
                              style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '11px', padding: 0 }}>
                        {wi.case.case_number}: {wi.case.title}
                      </button>
                    )}
                    <PriorityBadge priority={wi.priority} />
                  </div>
                </div>
                {/* SLA countdown */}
                {wi.sla_deadline && (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: slaColor(wi.sla_deadline) }}>
                      {slaText(wi.sla_deadline)}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>SLA</div>
                  </div>
                )}
                {/* Actions */}
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  {wi.status === 'available' && (
                    <button onClick={(e) => { e.stopPropagation(); handleClaim(wi.id); }}
                            style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>Claim</button>
                  )}
                  {wi.status === 'claimed' && (
                    <button onClick={(e) => { e.stopPropagation(); handleRelease(wi.id); }}
                            style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px', background: 'var(--bg-secondary)' }}>Release</button>
                  )}
                </div>
              </div>
            ))}

            {/* Flat tasks separator */}
            {flatTasks.length > 0 && items.length > 0 && (
              <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '8px 0 4px' }}>
                Simple Tasks
              </div>
            )}

            {/* Legacy flat tasks */}
            {flatTasks.map(t => (
              <div key={t.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="checkbox" checked={t.status === 'completed'}
                  onChange={() => t.status !== 'completed' && handleFlatComplete(t.id, t.case_id)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, textDecoration: t.status === 'completed' ? 'line-through' : 'none' }}>{t.title}</div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '3px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    <StatusBadge status={t.status} />
                    {t.case_number && <button onClick={() => onOpenCase(t.case_id)} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '11px', padding: 0 }}>{t.case_number}</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Work Item Detail (full-page form view) ───────────────────────────────
function WorkItemDetail({ workspaceId, workItemId, authHeaders, onBack, onComplete, onOpenCase }) {
  const [wi, setWi] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [cmErrors, setCmErrors] = useState({}); // case-manager rule errors keyed by field name
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  // Validate the form against case-manager rules (required_for_decisions /
  // required_for_roles) before posting. On errors, set inline banner state and
  // abort. The renderer already handles visible_for_roles / readonly_for_roles
  // so hidden-by-role fields are never collected and never required-checked.
  const handleDecisionClick = (decision) => {
    const errs = validateCaseManagerRules(
      wi?.form_definition,
      formValues,
      decision,
      wi?.actor_role_names || []
    );
    if (errs && Object.keys(errs).length > 0) {
      setCmErrors(errs);
      return;
    }
    setCmErrors({});
    onComplete(workItemId, formValues, decision);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${workItemId}/save`,
        { fields: formValues },
        authHeaders(),
      );
      setSavedAt(new Date());
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${workItemId}`, authHeaders());
        setWi(res.data);
        // Pre-fill form with case + task field values (case-insensitive key matching).
        // Task fields take precedence over case fields when both mappings target the
        // same form field — tasks are more specific to the step being completed.
        try {
          const cf = res.data.case?.custom_fields_json
            ? (typeof res.data.case.custom_fields_json === 'string' ? JSON.parse(res.data.case.custom_fields_json) : res.data.case.custom_fields_json)
            : {};
          const tf = res.data.task?.custom_fields_json
            ? (typeof res.data.task.custom_fields_json === 'string' ? JSON.parse(res.data.task.custom_fields_json) : res.data.task.custom_fields_json)
            : {};
          const cfLower = {};
          Object.entries(cf).forEach(([k, v]) => { cfLower[k.toLowerCase()] = v; });
          const tfLower = {};
          Object.entries(tf).forEach(([k, v]) => { tfLower[k.toLowerCase()] = v; });

          let caseWB = JSON.parse(res.data.case_field_writeback_json || '{}') || {};
          let taskWB = JSON.parse(res.data.task_field_writeback_json || '{}') || {};

          // Derive mappings from form definition / inline fields when not explicit.
          const allFields = [];
          if (res.data.form_definition && Array.isArray(res.data.form_definition.fields)) allFields.push(...res.data.form_definition.fields);
          if (res.data.inline_fields_json) {
            try { allFields.push(...JSON.parse(res.data.inline_fields_json)); } catch {}
          }
          if (Object.keys(caseWB).length === 0) {
            allFields.forEach(f => { if (f.name) caseWB[f.name] = f.case_field_key || f.name; });
          }
          if (Object.keys(taskWB).length === 0) {
            allFields.forEach(f => {
              const tkey = (f.task_field_key || '').trim();
              if (f.name && tkey) taskWB[f.name] = tkey;
            });
          }

          const prefill = {};
          for (const [formKey, caseKey] of Object.entries(caseWB)) {
            if (cf[caseKey] !== undefined) prefill[formKey] = cf[caseKey];
            else if (cfLower[caseKey.toLowerCase()] !== undefined) prefill[formKey] = cfLower[caseKey.toLowerCase()];
          }
          for (const [formKey, taskKey] of Object.entries(taskWB)) {
            if (tf[taskKey] !== undefined) prefill[formKey] = tf[taskKey];
            else if (tfLower[taskKey.toLowerCase()] !== undefined) prefill[formKey] = tfLower[taskKey.toLowerCase()];
          }
          // A previously saved draft (via /work-items/{id}/save) takes priority
          // over case/task field prefill — it represents the worker's most
          // recent in-progress state.
          try {
            const raw = res.data?.response_data_json;
            if (raw) {
              const draft = typeof raw === 'string' ? JSON.parse(raw) : raw;
              if (draft && typeof draft === 'object') Object.assign(prefill, draft);
            }
          } catch {}
          setFormValues(prefill);
        } catch {}
      } catch { setWi(null); }
      setLoading(false);
    })();
  }, [workItemId, workspaceId]);

  if (loading) return <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading work item...</div>;
  if (!wi) return <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Work item not found.</div>;

  const displayFields = JSON.parse(wi.case_field_display_json || '[]');
  const taskDisplayFields = JSON.parse(wi.task_field_display_json || '[]');
  const caseData = wi.case || {};
  const taskData = wi.task || {};
  const customFields = caseData.custom_fields_json ? (typeof caseData.custom_fields_json === 'string' ? JSON.parse(caseData.custom_fields_json) : caseData.custom_fields_json) : {};
  const taskCustomFields = taskData.custom_fields_json ? (typeof taskData.custom_fields_json === 'string' ? JSON.parse(taskData.custom_fields_json) : taskData.custom_fields_json) : {};
  // Case-insensitive custom field lookup helper
  const cfLowerMap = {};
  Object.entries(customFields).forEach(([k, v]) => { cfLowerMap[k.toLowerCase()] = v; });
  const getCaseField = (key) => customFields[key] !== undefined ? customFields[key] : cfLowerMap[key.toLowerCase()];
  const tfLowerMap = {};
  Object.entries(taskCustomFields).forEach(([k, v]) => { tfLowerMap[k.toLowerCase()] = v; });
  const getTaskField = (key) => taskCustomFields[key] !== undefined ? taskCustomFields[key] : tfLowerMap[key.toLowerCase()];
  const formDef = wi.form_definition || null;
  const inlineFields = wi.inline_fields_json ? JSON.parse(wi.inline_fields_json) : [];

  return (
    <div>
      {/* Back button */}
      <button onClick={onBack} style={{ ...btnSecondary, marginBottom: '16px', fontSize: '12px' }}>← Back to Inbox</button>

      {/* Case context card */}
      <div style={{ ...cardStyle, marginBottom: '16px', padding: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <button onClick={() => onOpenCase(wi.case_id)} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '14px', fontWeight: 600, padding: 0 }}>
              {caseData.case_number}: {caseData.title}
            </button>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', fontSize: '12px' }}>
              <StatusBadge status={caseData.status} />
              <PriorityBadge priority={caseData.priority} />
            </div>
          </div>
          {wi.sla_deadline && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: new Date(wi.sla_deadline) < new Date() ? '#ef4444' : '#22c55e' }}>
                {new Date(wi.sla_deadline) < new Date() ? 'SLA BREACHED' : `SLA: ${Math.ceil((new Date(wi.sla_deadline) - new Date()) / 3600000)}h remaining`}
              </div>
            </div>
          )}
        </div>
        {/* Read-only case + task fields */}
        {(displayFields.length > 0 || taskDisplayFields.length > 0) && (
          <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
            {displayFields.map(key => (
              <div key={`case-${key}`}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>case · {key}</div>
                <div style={{ fontSize: '13px', marginTop: '2px' }}>{getCaseField(key) ?? '—'}</div>
              </div>
            ))}
            {taskDisplayFields.map(key => (
              <div key={`task-${key}`}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>task · {key}</div>
                <div style={{ fontSize: '13px', marginTop: '2px' }}>{getTaskField(key) ?? '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Step info */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>{wi.step_name}</h3>
          {wi.role_name && (
            <span style={{ background: '#6366f120', color: '#6366f1', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 }}>
              {wi.role_name}
            </span>
          )}
        </div>
        {wi.step_description && <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary)' }}>{wi.step_description}</p>}
        {wi.step_instructions && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: '6px', padding: '10px', fontSize: '13px', marginBottom: '12px', borderLeft: '3px solid var(--accent-color)' }}>
            {wi.step_instructions}
          </div>
        )}
      </div>

      {/* Form */}
      <div style={{ ...cardStyle, padding: '16px', marginBottom: '16px' }}>
        {formDef ? (
          <XavvyFormRenderer
            definition={formDef}
            initialValues={formValues}
            onValuesChange={(vals) => { setFormValues(vals); if (Object.keys(cmErrors).length) setCmErrors({}); }}
            actorRoles={wi.actor_role_names || []}
          />
        ) : inlineFields.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {inlineFields.map(f => (
              <div key={f.name || f.key}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
                  {f.label || f.name}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                </label>
                {f.type === 'textarea' ? (
                  <textarea
                    value={formValues[f.name || f.key] || ''}
                    onChange={(e) => setFormValues(prev => ({ ...prev, [f.name || f.key]: e.target.value }))}
                    style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                    placeholder={f.placeholder}
                  />
                ) : f.type === 'select' ? (
                  <select
                    value={formValues[f.name || f.key] || ''}
                    onChange={(e) => setFormValues(prev => ({ ...prev, [f.name || f.key]: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="">Select...</option>
                    {(f.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : f.type === 'checkbox' ? (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                    <input
                      type="checkbox"
                      checked={!!formValues[f.name || f.key]}
                      onChange={(e) => setFormValues(prev => ({ ...prev, [f.name || f.key]: e.target.checked }))}
                    />
                    {f.placeholder || ''}
                  </label>
                ) : (
                  <input
                    type={(() => {
                      const t = (f.type || '').toLowerCase();
                      if (t === 'number') return 'number';
                      if (t === 'date') return 'date';
                      if (t === 'datetime' || t === 'datetime-local') return 'datetime-local';
                      if (t === 'time') return 'time';
                      if (t === 'email') return 'email';
                      if (t === 'url') return 'url';
                      if (t === 'password') return 'password';
                      if (t === 'tel' || t === 'phone') return 'tel';
                      return 'text';
                    })()}
                    value={formValues[f.name || f.key] || ''}
                    onChange={(e) => setFormValues(prev => ({ ...prev, [f.name || f.key]: f.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value }))}
                    style={inputStyle}
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>
            No form configured for this step. Click Complete to continue.
          </div>
        )}
      </div>

      {/* Case-manager rule errors */}
      {Object.keys(cmErrors).length > 0 && (
        <div style={{ marginBottom: 10, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 13 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Required fields are missing:</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {Object.entries(cmErrors).map(([fname, msg]) => (
              <li key={fname}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Action buttons — Save draft + decision branching or single complete */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {savedAt && (
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic', marginRight: 'auto' }}>
            Saved at {savedAt.toLocaleTimeString()}
          </span>
        )}
        {wi.status === 'claimed' && (
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            style={btnSecondary}
            title="Save form values without completing the step"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {(() => {
          let decisions = [];
          try { decisions = wi.decisions_json ? JSON.parse(wi.decisions_json) : []; } catch {}
          if (!Array.isArray(decisions) || decisions.length === 0) {
            return (
              <button onClick={() => handleDecisionClick('submit')} style={{ ...btnSecondary, background: 'var(--accent-color)', color: '#fff', fontWeight: 600 }}>
                Complete Step
              </button>
            );
          }
          return decisions.map(d => (
            <button
              key={d.value}
              onClick={() => handleDecisionClick(d.value)}
              style={{
                ...btnSecondary, fontWeight: 600,
                ...(d.style === 'success' ? { background: '#22c55e', color: '#fff', borderColor: '#22c55e' } :
                    d.style === 'danger' ? { background: '#ef4444', color: '#fff', borderColor: '#ef4444' } :
                    d.style === 'warning' ? { background: '#f59e0b', color: '#fff', borderColor: '#f59e0b' } :
                    { background: 'var(--accent-color)', color: '#fff' }),
              }}
            >
              {d.label || d.value}
            </button>
          ));
        })()}
        <button onClick={onBack} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Task Progress Tracker (stepper for workflow-driven tasks) ─────────────
function TaskProgressTracker({ task }) {
  if (!task.workflow_run_id && !task.task_type_id) return null;

  const totalSteps = task.total_steps || 0;
  const completedSteps = task.completed_steps || 0;
  const currentStep = task.current_step_name;
  const activeWi = task.active_work_item;

  if (totalSteps === 0 && !currentStep) {
    return (
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
        Workflow task — {task.status === 'completed' ? 'completed' : 'processing...'}
      </div>
    );
  }

  const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  return (
    <div>
      {/* Progress bar — only shown when we know the step count */}
      {totalSteps > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <div style={{ flex: 1, height: '6px', background: 'var(--bg-secondary)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(progress, 100)}%`, height: '100%', background: task.status === 'completed' ? '#22c55e' : 'var(--accent-color)', borderRadius: '3px', transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', flexShrink: 0 }}>
            {completedSteps}/{totalSteps} steps
          </span>
        </div>
      )}
      {/* Current step indicator */}
      {currentStep && task.status !== 'completed' && (
        <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'var(--accent-color)' }}>●</span>
          <span>{currentStep}</span>
          {activeWi && (
            <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
              — {activeWi.status === 'claimed' ? `claimed by ${activeWi.claimed_by_name || activeWi.claimed_by}` : 'available in queue'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Discretionary Task Palette ───────────────────────────────────────────
function DiscretionaryTaskPalette({ workspaceId, caseId, authHeaders, onRefresh }) {
  const [available, setAvailable] = useState([]);
  const [showPalette, setShowPalette] = useState(false);
  const [launching, setLaunching] = useState(null);
  // Launch form state — set when the user picks a task type that needs a form
  // (xavvy form OR auto-rendered task fields). null = no modal open.
  const [launchModal, setLaunchModal] = useState(null); // { tt, formDef|null, values }

  useEffect(() => {
    if (!showPalette || !workspaceId || !caseId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/available-tasks`, authHeaders());
        setAvailable(res.data || []);
      } catch { setAvailable([]); }
    })();
  }, [showPalette, workspaceId, caseId]);

  const submitLaunch = async (ttId, formData) => {
    setLaunching(ttId);
    try {
      const body = { task_type_id: ttId };
      if (formData && Object.keys(formData).length > 0) body.form_data = formData;
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/launch-task`, body, authHeaders());
      setShowPalette(false);
      setLaunchModal(null);
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to launch task'); }
    setLaunching(null);
  };

  const handleLaunch = async (tt) => {
    // Path A: explicit launch form configured.
    if (tt.launch_form_id) {
      try {
        const res = await axios.get(`${API_BASE_URL}/xavvy-forms/${tt.launch_form_id}`, authHeaders());
        setLaunchModal({ tt, formDef: res.data?.definition || null, values: {} });
      } catch { alert('Failed to load launch form'); }
      return;
    }
    // Path B: no form, but task type has custom fields → auto-render as single column.
    const fields = (tt.schema_json && Array.isArray(tt.schema_json.fields)) ? tt.schema_json.fields : [];
    if (fields.length > 0) {
      setLaunchModal({ tt, formDef: null, values: {} });
      return;
    }
    // Path C: nothing to collect — launch directly.
    submitLaunch(tt.id, null);
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      <button onClick={() => setShowPalette(!showPalette)} style={{ ...btnSecondary, fontSize: '12px' }}>
        {showPalette ? '— Hide' : '+ Launch Task'}
      </button>
      {showPalette && (
        <div style={{ marginTop: '8px', padding: '10px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          {available.length === 0 ? (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>No discretionary tasks available.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {available.map(tt => (
                <div key={tt.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderRadius: '6px', background: 'var(--bg-primary)' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 500, opacity: tt.can_add ? 1 : 0.5 }}>
                      {tt.icon !== 'checkbox' ? tt.icon + ' ' : ''}{tt.name}
                    </div>
                    {tt.description && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{tt.description}</div>}
                    {!tt.can_add && <div style={{ fontSize: '11px', color: '#ef4444' }}>{tt.cannot_add_reason}</div>}
                  </div>
                  <button
                    onClick={() => handleLaunch(tt)}
                    disabled={!tt.can_add || launching === tt.id}
                    style={{ ...btnSecondary, fontSize: '11px', padding: '3px 10px', opacity: tt.can_add ? 1 : 0.4 }}
                  >
                    {launching === tt.id ? '...' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Launch form modal — fired when the chosen task type has a launch_form_id
          OR has task fields the worker should fill before launch. */}
      {launchModal && (
        <div onClick={() => setLaunchModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 10, width: 'min(640px, 92vw)', maxHeight: '85vh', overflow: 'auto', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {launchModal.tt.icon !== 'checkbox' ? launchModal.tt.icon + ' ' : ''}{launchModal.tt.name}
              </div>
              <button onClick={() => setLaunchModal(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            {launchModal.tt.description && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>{launchModal.tt.description}</div>
            )}
            {launchModal.formDef ? (
              <XavvyFormRenderer
                definition={launchModal.formDef}
                initialValues={launchModal.values}
                onValuesChange={(vals) => setLaunchModal(m => m ? { ...m, values: vals } : m)}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(launchModal.tt.schema_json?.fields || []).map(f => (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                      {f.label || f.key}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                    </label>
                    {f.type === 'textarea' ? (
                      <textarea
                        value={launchModal.values[f.key] || ''}
                        onChange={(e) => setLaunchModal(m => ({ ...m, values: { ...m.values, [f.key]: e.target.value } }))}
                        style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                        placeholder={f.placeholder}
                      />
                    ) : f.type === 'select' ? (
                      <select
                        value={launchModal.values[f.key] || ''}
                        onChange={(e) => setLaunchModal(m => ({ ...m, values: { ...m.values, [f.key]: e.target.value } }))}
                        style={inputStyle}
                      >
                        <option value="">Select...</option>
                        {(f.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : f.type === 'boolean' || f.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={!!launchModal.values[f.key]}
                        onChange={(e) => setLaunchModal(m => ({ ...m, values: { ...m.values, [f.key]: e.target.checked } }))}
                      />
                    ) : (
                      <input
                        type={(() => {
                          const t = (f.type || '').toLowerCase();
                          if (t === 'number' || t === 'integer' || t === 'float') return 'number';
                          if (t === 'date') return 'date';
                          if (t === 'datetime' || t === 'datetime-local') return 'datetime-local';
                          if (t === 'time') return 'time';
                          if (t === 'email') return 'email';
                          if (t === 'url') return 'url';
                          if (t === 'password') return 'password';
                          if (t === 'tel' || t === 'phone') return 'tel';
                          return 'text';
                        })()}
                        value={launchModal.values[f.key] || ''}
                        onChange={(e) => setLaunchModal(m => ({ ...m, values: { ...m.values, [f.key]: e.target.value } }))}
                        style={inputStyle}
                        placeholder={f.placeholder}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-color)' }}>
              <button onClick={() => setLaunchModal(null)} style={btnSecondary}>Cancel</button>
              <button
                onClick={() => {
                  // For task-fields path, also enforce required-on-the-fly.
                  if (!launchModal.formDef) {
                    const fields = launchModal.tt.schema_json?.fields || [];
                    const missing = fields.filter(f => f.required && (launchModal.values[f.key] === undefined || launchModal.values[f.key] === '' || launchModal.values[f.key] === null));
                    if (missing.length) { alert(`Missing required fields: ${missing.map(f => f.label || f.key).join(', ')}`); return; }
                  }
                  submitLaunch(launchModal.tt.id, launchModal.values);
                }}
                disabled={launching === launchModal.tt.id}
                style={{ ...btnSecondary, background: 'var(--accent-color)', color: '#fff', fontWeight: 600 }}
              >
                {launching === launchModal.tt.id ? 'Launching…' : 'Launch'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Team Queues ──────────────────────────────────────────────────────────
function TeamQueues({ workspaceId, authHeaders, onOpenCase }) {
  const [teams, setTeams] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [activeQueue, setActiveQueue] = useState('unassigned');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) return;
    Promise.all([
      axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams`, authHeaders()).then(r => r.data).catch(() => []),
      axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/queues/unassigned`, authHeaders()).then(r => r.data).catch(() => ({ items: [] })),
      axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/queues/workload`, authHeaders()).then(r => r.data).catch(() => []),
    ]).then(([t, u, w]) => {
      setTeams(t || []);
      setUnassigned(u.items || []);
      setWorkload(w || []);
      setLoading(false);
    });
  }, [workspaceId]);

  const handleClaim = async (caseId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/queues/claim/${caseId}`, {}, authHeaders());
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/queues/unassigned`, authHeaders());
      setUnassigned(res.data.items || []);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to claim case'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading...</div>;

  return (
    <div>
      {/* Workload overview */}
      <h3 style={{ fontSize: '15px', margin: '0 0 12px' }}>Team Workload</h3>
      {workload.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          {workload.map(w => (
            <div key={w.user_id} style={{ ...cardStyle, cursor: 'default' }}>
              <div style={{ fontSize: '13px', fontWeight: 600 }}>{w.username || `User ${w.user_id}`}</div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '12px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Open: <strong style={{ color: 'var(--text-primary)' }}>{w.open_cases}</strong></span>
                {w.overdue_cases > 0 && <span style={{ color: '#ef4444' }}>Overdue: <strong>{w.overdue_cases}</strong></span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>No workload data.</p>
      )}

      {/* Queue tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border-color)', marginBottom: '12px' }}>
        <button onClick={() => setActiveQueue('unassigned')} style={tabStyle(activeQueue === 'unassigned')}>
          Unassigned ({unassigned.length})
        </button>
        {teams.map(t => (
          <button key={t.id} onClick={() => setActiveQueue(t.id)} style={tabStyle(activeQueue === t.id)}>
            {t.name} ({t.member_count || 0})
          </button>
        ))}
      </div>

      {/* Queue content */}
      {activeQueue === 'unassigned' ? (
        unassigned.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>No unassigned cases.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {unassigned.map(c => (
              <div key={c.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpenCase(c.id)}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)' }}>{c.case_number}</span>
                    <StatusBadge status={c.status} />
                    <PriorityBadge priority={c.priority} />
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 500 }}>{c.title}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {c.case_type?.icon} {c.case_type?.name} — {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                  </div>
                </div>
                <button onClick={() => handleClaim(c.id)} style={{ ...btnPrimary, padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                  Claim
                </button>
              </div>
            ))}
          </div>
        )
      ) : (
        <TeamQueueContent teamId={activeQueue} workspaceId={workspaceId} authHeaders={authHeaders} onOpenCase={onOpenCase} />
      )}
    </div>
  );
}

function TeamQueueContent({ teamId, workspaceId, authHeaders, onOpenCase }) {
  const [cases, setCases] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/queues/team/${teamId}`, authHeaders())
        .then(r => r.data.items || []).catch(() => []),
      axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/queues/team/${teamId}/work-items`, authHeaders())
        .then(r => r.data || []).catch(() => []),
    ]).then(([c, wi]) => { setCases(c); setWorkItems(wi); setLoading(false); });
  }, [teamId, workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleClaim = async (wiId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${wiId}/claim`, {}, authHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to claim'); }
  };

  const handleComplete = async (wiId, formData, decision) => {
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${wiId}/complete`, { fields: formData || {}, decision: decision || 'submit' }, authHeaders());
      setSelectedWorkItem(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to complete'); }
  };

  // Show WorkItemDetail when a work item is selected
  if (selectedWorkItem) {
    return (
      <WorkItemDetail
        workspaceId={workspaceId}
        workItemId={selectedWorkItem}
        authHeaders={authHeaders}
        onBack={() => { setSelectedWorkItem(null); load(); }}
        onComplete={handleComplete}
        onOpenCase={onOpenCase}
      />
    );
  }

  if (loading) return <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Loading...</div>;
  if (cases.length === 0 && workItems.length === 0) return <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>No open items in this team.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Work Items routed to this team */}
      {workItems.length > 0 && (
        <>
          <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '4px 0' }}>Work Items ({workItems.length})</div>
          {workItems.map(wi => (
            <div key={wi.id} style={{ ...cardStyle, cursor: 'pointer', borderLeft: `3px solid ${wi.priority === 'critical' ? '#ef4444' : wi.priority === 'high' ? '#f59e0b' : '#3b82f6'}` }}
                 onClick={() => setSelectedWorkItem(wi.id)}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>{wi.step_name}</span>
                <StatusBadge status={wi.status} />
                <PriorityBadge priority={wi.priority} />
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '11px', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                {wi.case?.case_type_name && (
                  <span style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: 500 }}>
                    {wi.case.case_type_icon || '📋'} {wi.case.case_type_name}
                  </span>
                )}
                {wi.task_type_name && (
                  <span style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '3px', padding: '1px 6px', fontSize: '10px', fontWeight: 500 }}>
                    {wi.task_type_name}
                  </span>
                )}
                <span>{wi.case?.case_number ? `${wi.case.case_number}: ${wi.case.title}` : `Case ${wi.case_id}`}</span>
                {wi.claimed_by && <span>claimed by {wi.claimed_by_name || wi.claimed_by}</span>}
              </div>
              <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                {wi.status === 'available' && (
                  <button onClick={(e) => { e.stopPropagation(); handleClaim(wi.id); }}
                          style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>Claim</button>
                )}
                <button onClick={(e) => { e.stopPropagation(); onOpenCase(wi.case_id); }}
                        style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>Open Case</button>
              </div>
            </div>
          ))}
        </>
      )}
      {/* Cases assigned to this team */}
      {cases.length > 0 && (
        <>
          {workItems.length > 0 && <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', padding: '4px 0' }}>Cases ({cases.length})</div>}
          {cases.map(c => (
            <div key={c.id} style={{ ...cardStyle, cursor: 'pointer' }} onClick={() => onOpenCase(c.id)}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)' }}>{c.case_number}</span>
                <StatusBadge status={c.status} />
                <PriorityBadge priority={c.priority} />
                <SLAIndicator sla={c.sla_resolution} label="SLA" />
              </div>
              <div style={{ fontSize: '13px', fontWeight: 500 }}>{c.title}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                {c.case_type?.icon} {c.case_type?.name}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ─── CSV Import Dialog ────────────────────────────────────────────────────
function ImportCSVDialog({ workspaceId, caseTypes, authHeaders, onClose, onComplete }) {
  const [step, setStep] = useState('upload'); // upload, map, importing, done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedCaseType, setSelectedCaseType] = useState('');
  const [columnMap, setColumnMap] = useState({});
  const [defaultStatus, setDefaultStatus] = useState('open');
  const [defaultPriority, setDefaultPriority] = useState('medium');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const formData = new FormData();
    formData.append('file', f);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/import/preview`,
        formData,
        { ...authHeaders(), headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } }
      );
      setPreview(res.data);
      setColumnMap(res.data.suggested_map || {});
      setStep('map');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to parse CSV');
    }
  };

  const handleImport = async () => {
    if (!file || !selectedCaseType) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const params = new URLSearchParams({
        column_map: JSON.stringify(columnMap),
        case_type_id: selectedCaseType,
        default_status: defaultStatus,
        default_priority: defaultPriority,
      });
      const res = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/import/execute?${params}`,
        formData,
        { ...authHeaders(), headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } }
      );
      setResult(res.data);
      setStep('done');
    } catch (err) {
      alert(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  // Available target fields
  const ct = caseTypes.find(t => t.id === selectedCaseType);
  const schemaFields = ct?.schema_json?.fields || [];
  const targetOptions = [
    { value: '', label: '— Skip column —' },
    { value: 'title', label: 'Title' },
    { value: 'description', label: 'Description' },
    { value: 'priority', label: 'Priority' },
    { value: 'status', label: 'Status' },
    ...schemaFields.map(f => ({ value: `cf:${f.key}`, label: `CF: ${f.label || f.key}` })),
  ];

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalContent, width: '700px', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>Import Cases from CSV</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px' }}>x</button>
        </div>

        {step === 'upload' && (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>CSV</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
              Upload a CSV file to import cases. The first row should contain column headers.
            </p>
            <label style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              Select CSV File
              <input type="file" accept=".csv,.tsv,.txt" onChange={handleFileSelect} style={{ display: 'none' }} />
            </label>
          </div>
        )}

        {step === 'map' && preview && (
          <div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {preview.total_rows} rows found. Map CSV columns to case fields:
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Case Type *</label>
              <select value={selectedCaseType} onChange={e => setSelectedCaseType(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Select case type...</option>
                {caseTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.icon} {ct.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Default Status</label>
                <select value={defaultStatus} onChange={e => setDefaultStatus(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                  {['open', 'in_progress', 'under_review', 'resolved', 'closed'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Default Priority</label>
                <select value={defaultPriority} onChange={e => setDefaultPriority(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                  {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto', maxHeight: '300px', marginBottom: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>CSV Column</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Maps To</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Sample</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.columns || []).map(col => (
                    <tr key={col} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 500 }}>{col}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={columnMap[col] || ''} onChange={e => setColumnMap(prev => ({ ...prev, [col]: e.target.value }))} style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px', cursor: 'pointer' }}>
                          {targetOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--text-tertiary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {preview.preview_rows?.[0]?.[col] || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setStep('upload'); setPreview(null); setFile(null); }} style={btnSecondary}>Back</button>
              <button onClick={handleImport} style={btnPrimary} disabled={!selectedCaseType || importing || !Object.values(columnMap).includes('title')}>
                {importing ? 'Importing...' : `Import ${preview.total_rows} Rows`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px', color: '#10b981' }}>OK</div>
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>{result.created} cases imported</div>
            {result.total_errors > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '8px' }}>{result.total_errors} rows had errors:</div>
                <div style={{ maxHeight: '150px', overflow: 'auto', textAlign: 'left', background: 'var(--bg-tertiary)', borderRadius: '6px', padding: '8px', fontSize: '12px' }}>
                  {(result.errors || []).map((e, i) => (
                    <div key={i} style={{ color: 'var(--text-secondary)', padding: '2px 0' }}>Row {e.row}: {e.error}</div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => { onComplete(); onClose(); }} style={btnPrimary}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Portal: Track Case ────────────────────────────────────────────────────
function PortalTrackCase() {
  const [caseNumber, setCaseNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async () => {
    if (!caseNumber.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/portal/track/${encodeURIComponent(caseNumber.trim())}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Case not found. Please check the case number.');
    }
    setLoading(false);
  };

  const statusColor = { open: '#3b82f6', in_progress: '#f59e0b', under_review: '#8b5cf6', resolved: '#10b981', closed: '#6b7280' };

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
        <h3 style={{ margin: '0 0 4px', fontSize: '18px' }}>Track Your Case</h3>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>Enter your case number to check its current status</p>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input
          value={caseNumber}
          onChange={e => setCaseNumber(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleTrack(); }}
          style={{ ...inputStyle, flex: 1, fontSize: '14px', padding: '10px 14px' }}
          placeholder="e.g. CLAIM-2026-000001"
          autoFocus
        />
        <button onClick={handleTrack} style={{ ...btnPrimary, padding: '10px 20px' }} disabled={loading || !caseNumber.trim()}>
          {loading ? 'Looking up...' : 'Track'}
        </button>
      </div>
      {error && <div style={{ padding: '12px', borderRadius: '8px', background: '#ef444420', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}
      {result && (
        <div style={{ ...cardStyle, cursor: 'default', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--accent-color)', marginBottom: '4px' }}>{result.case_number}</div>
              <h4 style={{ margin: 0, fontSize: '16px' }}>{result.title}</h4>
              {result.case_type && (
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{result.case_type_icon} {result.case_type}</div>
              )}
            </div>
            <div style={{
              padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
              background: `${statusColor[result.status] || '#6b7280'}20`,
              color: statusColor[result.status] || '#6b7280',
            }}>
              {result.status_label}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div><div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Priority</div><div style={{ fontSize: '13px', textTransform: 'capitalize' }}>{result.priority}</div></div>
            <div><div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Created</div><div style={{ fontSize: '13px' }}>{result.created_at ? new Date(result.created_at).toLocaleDateString() : '—'}</div></div>
            {result.closed_at && (
              <>
                <div><div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Closed</div><div style={{ fontSize: '13px' }}>{new Date(result.closed_at).toLocaleDateString()}</div></div>
                {result.resolution_category && (
                  <div><div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Resolution</div><div style={{ fontSize: '13px' }}>{result.resolution_category}</div></div>
                )}
              </>
            )}
          </div>
          {result.resolution_notes && (
            <div style={{ padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
              {result.resolution_notes}
            </div>
          )}
          {/* Status Timeline */}
          {result.timeline?.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Status History</div>
              {result.timeline.map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '12px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-color)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {typeof t.old_value === 'string' ? t.old_value.replace(/_/g, ' ') : '—'} → {typeof t.new_value === 'string' ? t.new_value.replace(/_/g, ' ') : '—'}
                  </span>
                  <span style={{ color: 'var(--text-tertiary)', marginLeft: 'auto', fontSize: '11px' }}>
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Public Comments */}
          {result.comments?.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Updates</div>
              {result.comments.map((cm, i) => (
                <div key={i} style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: '6px', marginBottom: '6px', fontSize: '13px' }}>
                  <div style={{ lineHeight: 1.5 }}>{cm.content}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                    {cm.created_at ? new Date(cm.created_at).toLocaleString() : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function CaseManager({ onBack, onManageTypes, workspaceId, isAdmin, navTarget, onNavTargetConsumed }) {
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const [cases, setCases] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCaseType, setFilterCaseType] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [mainView, setMainView] = useState('cases'); // cases, my_tasks, queues, search
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  // Bulk operations
  const [selectedCaseIds, setSelectedCaseIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [caseListMode, setCaseListMode] = useState('list'); // list, board, calendar
  const [customFieldFilters, setCustomFieldFilters] = useState({});
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [focusedCaseIdx, setFocusedCaseIdx] = useState(-1);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const pageSize = 50;

  // Deep-link from notifications: open a specific case or work item
  useEffect(() => {
    if (!navTarget) return;
    if (navTarget.work_item_id) {
      setMainView('my_tasks');
      // MyWorkInbox will pick up initialWorkItemId
    } else if (navTarget.case_id) {
      setSelectedCaseId(navTarget.case_id);
    }
    onNavTargetConsumed?.();
  }, [navTarget]);

  const loadCases = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterCaseType) params.set('case_type_id', filterCaseType);
      const activeCfFilters = Object.fromEntries(Object.entries(customFieldFilters).filter(([, v]) => v));
      if (Object.keys(activeCfFilters).length > 0) params.set('custom_field_filters', JSON.stringify(activeCfFilters));

      const [casesRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases?${params}`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/dashboard`, getAuthHeaders()),
      ]);
      setCases(casesRes.data.items || []);
      setTotal(casesRes.data.total || 0);
      setStats(statsRes.data || {});
    } catch (err) {
      console.error('Failed to load cases', err);
      if (err.response?.status === 402) setCases([]);
    }
    setLoading(false);
  }, [workspaceId, page, search, filterStatus, filterPriority, filterCaseType, sortBy, sortOrder, customFieldFilters]);

  const loadCaseTypes = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types`, getAuthHeaders());
      setCaseTypes(res.data || []);
    } catch { setCaseTypes([]); }
  }, [workspaceId]);

  const loadSavedViews = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-views`, getAuthHeaders());
      setSavedViews(res.data || []);
    } catch { setSavedViews([]); }
  }, [workspaceId]);

  const handleGlobalSearch = useCallback(async (q) => {
    if (!workspaceId || !q || q.length < 2) { setGlobalSearchResults(null); return; }
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/search?q=${encodeURIComponent(q)}&limit=30`, getAuthHeaders());
      setGlobalSearchResults(res.data);
    } catch { setGlobalSearchResults(null); }
    setSearchLoading(false);
  }, [workspaceId]);

  const handleSaveCurrentView = async () => {
    if (!workspaceId) return;
    const name = prompt('Save current filters as view:');
    if (!name) return;
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-views`, {
        name,
        filters_json: { search, filterStatus, filterPriority, filterCaseType, sortBy, sortOrder },
        view_type: 'list',
        is_pinned: false,
      }, getAuthHeaders());
      loadSavedViews();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save view'); }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedCaseIds.size === 0) return;
    if (bulkAction === 'delete' && !confirm(`Delete ${selectedCaseIds.size} case(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/bulk`, {
        case_ids: Array.from(selectedCaseIds),
        action: bulkAction,
        value: bulkValue || null,
      }, getAuthHeaders());
      setSelectedCaseIds(new Set());
      setBulkAction('');
      setBulkValue('');
      loadCases();
    } catch (err) { alert(err.response?.data?.detail || 'Bulk action failed'); }
    setBulkLoading(false);
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterCaseType) params.set('case_type_id', filterCaseType);
      if (search) params.set('search', search);
      const activeCf = Object.fromEntries(Object.entries(customFieldFilters).filter(([, v]) => v));
      if (Object.keys(activeCf).length > 0) params.set('custom_field_filters', JSON.stringify(activeCf));
      const res = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/export/csv?${params}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cases-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Export failed');
    }
  };

  const toggleCaseSelection = (caseId) => {
    setSelectedCaseIds(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCaseIds.size === cases.length) setSelectedCaseIds(new Set());
    else setSelectedCaseIds(new Set(cases.map(c => c.id)));
  };

  const applyView = (view) => {
    const f = view.filters_json || {};
    setSearch(f.search || '');
    setFilterStatus(f.filterStatus || '');
    setFilterPriority(f.filterPriority || '');
    setFilterCaseType(f.filterCaseType || '');
    setSortBy(f.sortBy || 'created_at');
    setSortOrder(f.sortOrder || 'desc');
    setActiveViewId(view.id);
    setPage(1);
    setMainView('cases');
  };

  const handleTogglePinView = async (viewId) => {
    const view = savedViews.find(v => v.id === viewId);
    if (!view) return;
    try {
      await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-views/${viewId}`, {
        is_pinned: !view.is_pinned,
      }, getAuthHeaders());
      loadSavedViews();
    } catch { /* ignore */ }
  };

  const handleDeleteView = async (viewId) => {
    if (!confirm('Delete this saved view?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-views/${viewId}`, getAuthHeaders());
      if (activeViewId === viewId) setActiveViewId(null);
      loadSavedViews();
    } catch { /* ignore */ }
  };

  useEffect(() => { loadCases(); }, [loadCases]);
  useEffect(() => { loadCaseTypes(); }, [loadCaseTypes]);
  useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      // Don't fire when typing in inputs/textareas/selects
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // ? — show keyboard help
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowKeyboardHelp(prev => !prev);
        return;
      }
      // Escape — close modals or go back
      if (e.key === 'Escape') {
        if (showKeyboardHelp) { setShowKeyboardHelp(false); return; }
        if (showCreate) { setShowCreate(false); return; }
        if (showImportDialog) { setShowImportDialog(false); return; }
        if (selectedCaseId) { setSelectedCaseId(null); loadCases(); return; }
        return;
      }
      // Only J/K/Enter work on the case list view
      if (mainView !== 'cases' || selectedCaseId || showCreate || showImportDialog) return;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedCaseIdx(prev => Math.min(prev + 1, cases.length - 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedCaseIdx(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && focusedCaseIdx >= 0 && focusedCaseIdx < cases.length) {
        e.preventDefault();
        setSelectedCaseId(cases[focusedCaseIdx].id);
      } else if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowCreate(true);
      } else if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setMainView('search');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mainView, selectedCaseId, showCreate, showImportDialog, showKeyboardHelp, cases, focusedCaseIdx]);

  // Reset focus when cases change
  useEffect(() => { setFocusedCaseIdx(-1); }, [cases]);

  const handleCreate = async (data) => {
    if (!workspaceId) { alert('No workspace selected. Please select a workspace first.'); return; }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases`, data, getAuthHeaders());
      setShowCreate(false);
      setSelectedCaseId(res.data.id);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create case'); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ── Case Detail ──────────────────────────────────────────────────────────
  if (selectedCaseId) {
    return (
      <CaseDetail
        caseId={selectedCaseId}
        workspaceId={workspaceId}
        authHeaders={getAuthHeaders}
        onBack={() => { setSelectedCaseId(null); loadCases(); }}
        onRefresh={loadCases}
      />
    );
  }

  // ── Case List ────────────────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '16px' }}>📋 Case Manager</h2>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{total} case{total !== 1 ? 's' : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {workspaceId && (
            <button
              onClick={() => window.open(`/case-client/${workspaceId}`, '_blank', 'noopener,noreferrer')}
              style={btnSecondary}
              title="Open the worker-facing Case Client for this workspace in a new tab"
            >
              ↗ Case Client
            </button>
          )}
          {isAdmin && onManageTypes && (
            <button onClick={onManageTypes} style={btnSecondary} title="Admin: Case Types, Teams, Calendars, Notifications">
              ⚙ Admin
            </button>
          )}
          <button onClick={() => setShowImportDialog(true)} style={btnSecondary} title="Import cases from CSV">
            Import CSV
          </button>
          {cases.length > 0 && (
            <button onClick={handleExportCSV} style={btnSecondary} title="Export current view to CSV">
              Export CSV
            </button>
          )}
          <button onClick={() => setShowCreate(true)} style={btnPrimary} disabled={caseTypes.length === 0}>
            + New Case
          </button>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', padding: '0 20px', background: 'var(--bg-secondary)' }}>
        {[
          { key: 'cases', label: 'All Cases' },
          { key: 'my_tasks', label: 'My Work' },
          { key: 'queues', label: 'Queues' },
          { key: 'search', label: 'Search' },
          { key: 'portal', label: 'Track Case' },
        ].map(v => (
          <button key={v.key} onClick={() => setMainView(v.key)} style={tabStyle(mainView === v.key)}>
            {v.label}
          </button>
        ))}
        {/* Saved views pills */}
        {savedViews.filter(v => v.is_pinned).map(sv => (
          <button
            key={sv.id}
            onClick={() => applyView(sv)}
            style={{
              ...tabStyle(activeViewId === sv.id),
              fontSize: '12px',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            📌 {sv.name}
          </button>
        ))}
      </div>

      <div style={bodyStyle}>
        {/* Dashboard — only on Cases tab */}
        {mainView === 'cases' && <CaseDashboard stats={stats} workspaceId={workspaceId} getAuthHeaders={getAuthHeaders} />}

        {/* My Work view (Phase 7 — unified inbox with in-baskets) */}
        {mainView === 'my_tasks' && (
          <MyWorkInbox
            workspaceId={workspaceId}
            authHeaders={getAuthHeaders}
            onOpenCase={(id) => setSelectedCaseId(id)}
            initialWorkItemId={navTarget?.work_item_id}
          />
        )}

        {/* Queues view */}
        {mainView === 'queues' && (
          <TeamQueues
            workspaceId={workspaceId}
            authHeaders={getAuthHeaders}
            onOpenCase={(id) => setSelectedCaseId(id)}
          />
        )}

        {/* Global Search view */}
        {mainView === 'search' && (
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
              <input
                value={globalSearchQuery}
                onChange={e => setGlobalSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleGlobalSearch(globalSearchQuery); }}
                style={{ ...inputStyle, maxWidth: '400px' }}
                placeholder="Search across cases, comments, tasks, and documents..."
                autoFocus
              />
              <button onClick={() => handleGlobalSearch(globalSearchQuery)} style={btnPrimary} disabled={searchLoading || globalSearchQuery.length < 2}>
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
            {globalSearchResults && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Cases */}
                {globalSearchResults.cases?.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)' }}>Cases ({globalSearchResults.cases.length})</h3>
                    {globalSearchResults.cases.map(c => (
                      <div key={c.id} onClick={() => setSelectedCaseId(c.id)} style={{ ...cardStyle, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--accent-color)' }}>{c.case_number}</span>
                        <span style={{ flex: 1, fontSize: '13px' }}>{c.title}</span>
                        <StatusBadge status={c.status} />
                        <PriorityBadge priority={c.priority} />
                      </div>
                    ))}
                  </div>
                )}
                {/* Comments */}
                {globalSearchResults.comments?.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)' }}>Comments ({globalSearchResults.comments.length})</h3>
                    {globalSearchResults.comments.map(cm => (
                      <div key={cm.id} onClick={() => setSelectedCaseId(cm.case_id)} style={{ ...cardStyle, marginBottom: '4px', cursor: 'pointer', borderLeft: `3px solid ${cm.is_internal ? '#f59e0b' : '#3b82f6'}` }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{cm.user_name} on case</div>
                        <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', maxHeight: '60px', overflow: 'hidden' }}><CommentContent text={cm.content} /></div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Tasks */}
                {globalSearchResults.tasks?.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)' }}>Tasks ({globalSearchResults.tasks.length})</h3>
                    {globalSearchResults.tasks.map(t => (
                      <div key={t.id} onClick={() => setSelectedCaseId(t.case_id)} style={{ ...cardStyle, marginBottom: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', flex: 1 }}>{t.title}</span>
                        <Badge color={t.status === 'completed' ? '#10b981' : '#f59e0b'}>{t.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
                {/* Documents */}
                {globalSearchResults.documents?.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-secondary)' }}>Documents ({globalSearchResults.documents.length})</h3>
                    {globalSearchResults.documents.map(d => (
                      <div key={d.id} onClick={() => setSelectedCaseId(d.case_id)} style={{ ...cardStyle, marginBottom: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px' }}>📄</span>
                        <span style={{ fontSize: '13px', flex: 1 }}>{d.filename}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{d.folder || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* No results */}
                {!globalSearchResults.cases?.length && !globalSearchResults.comments?.length && !globalSearchResults.tasks?.length && !globalSearchResults.documents?.length && (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>No results found.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Portal / Track Case view */}
        {mainView === 'portal' && (
          <PortalTrackCase />
        )}

        {mainView === 'cases' && (<>
        {/* View mode toggle */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
          {[
            { key: 'list', icon: '☰', label: 'List' },
            { key: 'board', icon: '▦', label: 'Board' },
            { key: 'calendar', icon: '📅', label: 'Calendar' },
          ].map(m => (
            <button key={m.key} onClick={() => setCaseListMode(m.key)}
              style={{
                padding: '5px 12px', fontSize: '12px', borderRadius: '6px', cursor: 'pointer',
                border: caseListMode === m.key ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
                background: caseListMode === m.key ? 'var(--accent-color)15' : 'var(--bg-tertiary)',
                color: caseListMode === m.key ? 'var(--accent-color)' : 'var(--text-secondary)',
                fontWeight: caseListMode === m.key ? 600 : 400,
              }}
            >
              {m.icon} {m.label}
            </button>
          ))}
        </div>

        {/* Saved views bar */}
        {savedViews.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginRight: '4px' }}>Views:</span>
            {savedViews.map(sv => (
              <span key={sv.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', background: activeViewId === sv.id ? 'var(--accent-color)' : 'var(--bg-tertiary)', color: activeViewId === sv.id ? '#fff' : 'var(--text-primary)', cursor: 'pointer' }}>
                <span onClick={() => applyView(sv)}>{sv.name}</span>
                <span onClick={() => handleTogglePinView(sv.id)} style={{ cursor: 'pointer', opacity: 0.6 }} title={sv.is_pinned ? 'Unpin' : 'Pin'}>{sv.is_pinned ? '📌' : '📍'}</span>
                <span onClick={() => handleDeleteView(sv.id)} style={{ cursor: 'pointer', opacity: 0.4, fontSize: '10px' }} title="Delete">✕</span>
              </span>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ ...inputStyle, maxWidth: '240px' }}
            placeholder="Search cases..."
          />
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: '150px', cursor: 'pointer' }}>
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="under_review">Under Review</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select value={filterPriority} onChange={e => { setFilterPriority(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: '140px', cursor: 'pointer' }}>
            <option value="">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          {caseTypes.length > 0 && (
            <select value={filterCaseType} onChange={e => { setFilterCaseType(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: '180px', cursor: 'pointer' }}>
              <option value="">All Types</option>
              {caseTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.icon} {ct.name}</option>)}
            </select>
          )}
          <select value={`${sortBy}:${sortOrder}`} onChange={e => { const [sb, so] = e.target.value.split(':'); setSortBy(sb); setSortOrder(so); setPage(1); }} style={{ ...inputStyle, maxWidth: '180px', cursor: 'pointer' }}>
            <option value="created_at:desc">Newest First</option>
            <option value="created_at:asc">Oldest First</option>
            <option value="updated_at:desc">Recently Updated</option>
            <option value="priority:desc">Priority (High→Low)</option>
            <option value="status:asc">Status</option>
          </select>
          <button onClick={handleSaveCurrentView} style={{ ...btnSecondary, padding: '6px 10px', fontSize: '11px' }} title="Save current filters as a view">
            Save View
          </button>
          {Object.keys(customFieldFilters).some(k => customFieldFilters[k]) && (
            <button onClick={() => { setCustomFieldFilters({}); setPage(1); }} style={{ ...btnSecondary, padding: '6px 10px', fontSize: '11px', color: '#ef4444' }}>
              Clear Field Filters
            </button>
          )}
        </div>
        {/* Custom field filters — shown when a case type is selected */}
        {filterCaseType && (() => {
          const ct = caseTypes.find(t => t.id === filterCaseType);
          const schema = ct?.schema_json;
          const fields = schema?.fields || [];
          const filterableFields = fields.filter(f => f.type === 'select' || f.type === 'dropdown' || f.type === 'text' || f.type === 'number' || f.type === 'boolean');
          if (filterableFields.length === 0) return null;
          return (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center', padding: '8px 12px', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>Field Filters:</span>
              {filterableFields.map(f => {
                if (f.type === 'select' || f.type === 'dropdown') {
                  return (
                    <select key={f.key} value={customFieldFilters[f.key] || ''} onChange={e => { setCustomFieldFilters(prev => ({ ...prev, [f.key]: e.target.value })); setPage(1); }} style={{ ...inputStyle, maxWidth: '160px', cursor: 'pointer', fontSize: '12px' }}>
                      <option value="">{f.label || f.key}</option>
                      {(f.options || []).map(o => {
                        const val = typeof o === 'object' ? o.value : o;
                        const lbl = typeof o === 'object' ? (o.label || o.value) : o;
                        return <option key={val} value={val}>{lbl}</option>;
                      })}
                    </select>
                  );
                }
                if (f.type === 'boolean') {
                  return (
                    <select key={f.key} value={customFieldFilters[f.key] || ''} onChange={e => { setCustomFieldFilters(prev => ({ ...prev, [f.key]: e.target.value })); setPage(1); }} style={{ ...inputStyle, maxWidth: '140px', cursor: 'pointer', fontSize: '12px' }}>
                      <option value="">{f.label || f.key}</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  );
                }
                return (
                  <input key={f.key} value={customFieldFilters[f.key] || ''} onChange={e => { setCustomFieldFilters(prev => ({ ...prev, [f.key]: e.target.value })); }} onKeyDown={e => { if (e.key === 'Enter') setPage(1); }} style={{ ...inputStyle, maxWidth: '160px', fontSize: '12px' }} placeholder={f.label || f.key} />
                );
              })}
            </div>
          );
        })()}

        {/* Bulk action bar */}
        {selectedCaseIds.size > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', padding: '10px 14px', background: 'var(--accent-color)10', border: '1px solid var(--accent-color)40', borderRadius: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>{selectedCaseIds.size} selected</span>
            <select value={bulkAction} onChange={e => { setBulkAction(e.target.value); setBulkValue(''); }} style={{ ...inputStyle, width: '160px', cursor: 'pointer' }}>
              <option value="">Choose action...</option>
              <option value="change_status">Change Status</option>
              <option value="change_priority">Change Priority</option>
              <option value="delete">Delete</option>
            </select>
            {bulkAction === 'change_status' && (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inputStyle, width: '150px', cursor: 'pointer' }}>
                <option value="">Select status...</option>
                {['open', 'in_progress', 'under_review', 'resolved', 'closed'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            )}
            {bulkAction === 'change_priority' && (
              <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inputStyle, width: '140px', cursor: 'pointer' }}>
                <option value="">Select priority...</option>
                {['critical', 'high', 'medium', 'low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            )}
            <button onClick={handleBulkAction} style={{ ...btnPrimary, padding: '6px 14px', fontSize: '12px', ...(bulkAction === 'delete' ? { background: '#ef4444' } : {}) }} disabled={!bulkAction || (bulkAction !== 'delete' && !bulkValue) || bulkLoading}>
              {bulkLoading ? 'Applying...' : 'Apply'}
            </button>
            <button onClick={() => { setSelectedCaseIds(new Set()); setBulkAction(''); setBulkValue(''); }} style={{ ...btnSecondary, padding: '6px 10px', fontSize: '12px' }}>
              Clear
            </button>
          </div>
        )}

        {/* Cases Content — List / Board / Calendar */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading cases...</div>
        ) : cases.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
            <div style={{ fontSize: '15px', marginBottom: '8px' }}>No cases found</div>
            <div style={{ fontSize: '13px' }}>
              {caseTypes.length === 0
                ? (onManageTypes
                    ? <span>Create a case type first. <button onClick={onManageTypes} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px', padding: 0 }}>Go to Case Types</button></span>
                    : 'Create a case type first to start managing cases.')
                : 'Click "+ New Case" to create your first case.'}
            </div>
          </div>
        ) : caseListMode === 'board' ? (
          <CaseKanbanBoard
            cases={cases}
            onOpenCase={id => setSelectedCaseId(id)}
            onStatusChange={async (caseId, newStatus) => {
              try {
                await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/status`, { status: newStatus }, getAuthHeaders());
                loadCases();
              } catch (err) { alert(err.response?.data?.detail || 'Status change failed'); }
            }}
          />
        ) : caseListMode === 'calendar' ? (
          <CaseCalendarView cases={cases} onOpenCase={id => setSelectedCaseId(id)} />
        ) : (
          <>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ padding: '10px 8px', width: '32px' }}>
                      <input type="checkbox" checked={cases.length > 0 && selectedCaseIds.size === cases.length} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Case #</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Title</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Type</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Priority</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c, cIdx) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedCaseId(c.id)}
                      style={{
                        borderBottom: '1px solid var(--border-color)', cursor: 'pointer', transition: 'background 0.15s',
                        background: cIdx === focusedCaseIdx ? 'var(--accent-color)12' : 'transparent',
                        outline: cIdx === focusedCaseIdx ? '2px solid var(--accent-color)40' : 'none',
                        outlineOffset: '-2px',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; setFocusedCaseIdx(cIdx); }}
                      onMouseLeave={e => { e.currentTarget.style.background = cIdx === focusedCaseIdx ? 'var(--accent-color)12' : 'transparent'; }}
                    >
                      <td style={{ padding: '10px 8px', width: '32px' }} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedCaseIds.has(c.id)} onChange={() => toggleCaseSelection(c.id)} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--accent-color)' }}>{c.case_number}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 500, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</td>
                      <td style={{ padding: '10px 12px', fontSize: '12px' }}>{c.case_type?.icon} {c.case_type?.name || '—'}</td>
                      <td style={{ padding: '10px 12px' }}><StatusBadge status={c.status} /></td>
                      <td style={{ padding: '10px 12px' }}><PriorityBadge priority={c.priority} /></td>
                      <td style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-secondary)' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px', alignItems: 'center' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={btnSecondary}>Prev</button>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={btnSecondary}>Next</button>
              </div>
            )}
          </>
        )}
        </>)}
      </div>

      {/* Create modal */}
      {showCreate && caseTypes.length > 0 && (
        <CreateCaseModal caseTypes={caseTypes} onClose={() => setShowCreate(false)} onCreate={handleCreate} authHeaders={getAuthHeaders} />
      )}

      {/* Import CSV dialog */}
      {showImportDialog && (
        <ImportCSVDialog
          workspaceId={workspaceId}
          caseTypes={caseTypes}
          authHeaders={getAuthHeaders}
          onClose={() => setShowImportDialog(false)}
          onComplete={loadCases}
        />
      )}

      {/* Keyboard shortcut hint */}
      <div style={{ position: 'fixed', bottom: '12px', right: '12px', fontSize: '11px', color: 'var(--text-tertiary)', background: 'var(--bg-secondary)', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', opacity: 0.7 }}>
        Press <kbd style={{ padding: '1px 4px', borderRadius: '3px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', fontSize: '10px' }}>?</kbd> for keyboard shortcuts
      </div>

      {/* Keyboard help overlay */}
      {showKeyboardHelp && (
        <div style={modalOverlay} onClick={() => setShowKeyboardHelp(false)}>
          <div style={{ ...modalContent, width: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Keyboard Shortcuts</h3>
              <button onClick={() => setShowKeyboardHelp(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px' }}>x</button>
            </div>
            {[
              { section: 'Navigation', items: [
                { key: 'j / ↓', desc: 'Move down in case list' },
                { key: 'k / ↑', desc: 'Move up in case list' },
                { key: 'Enter', desc: 'Open selected case' },
                { key: 'Esc', desc: 'Go back / close modal' },
              ]},
              { section: 'Actions', items: [
                { key: 'n', desc: 'New case' },
                { key: '/', desc: 'Search' },
                { key: '?', desc: 'Toggle this help' },
              ]},
            ].map(s => (
              <div key={s.section} style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: '8px' }}>{s.section}</div>
                {s.items.map(item => (
                  <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{item.desc}</span>
                    <kbd style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{item.key}</kbd>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
